import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import Sidebar from "./Sidebar";
import DashNavbar from "./DashNavbar";
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../css/JobPosting.css';

const Font = Quill.import('formats/font');
Font.whitelist = ['arial', 'georgia', 'comic-sans-ms', 'courier-new', 'helvetica', 'lucida'];
Quill.register(Font, true);

const stripHtml = (html) => {
  if (!html || html === '<p><br></p>' || html === '<p></p>') return "";
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || "";
};

const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }, { 'font': Font.whitelist }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }], [{ 'indent': '-1'}, { 'indent': '+1' }],
    ['link'], ['clean']
  ],
};

const quillFormats = [
  'header', 'font', 'bold', 'italic', 'underline', 'strike', 
  'list', 'bullet', 'indent', 'link'
];

const apiBaseUrl = "https://raasbackend.vetrinahealthcare.com";

const Jobposting = () => {
  const [postType, setPostType] = useState("job");
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Form states
  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState("");

  // Job Opening fields
  const [jobId, setJobId] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [jobVacancies, setJobVacancies] = useState("");
  const [jobExperience, setJobExperience] = useState("");
  const [jobLocation, setJobLocation] = useState("");
  const [jobRoleType, setJobRoleType] = useState("");
  
  // Job Opening errors
  const [jobTitleError, setJobTitleError] = useState("");
  const [jobVacanciesError, setJobVacanciesError] = useState("");
  const [jobExperienceError, setJobExperienceError] = useState("");
  const [jobLocationError, setJobLocationError] = useState("");
  const [jobRoleTypeError, setJobRoleTypeError] = useState("");

  // Business Opportunity fields
  const [businessId, setBusinessId] = useState("");
  const [businessTitle, setBusinessTitle] = useState("");
  const [natureOfBusiness, setNatureOfBusiness] = useState("");
  const [businessLocations, setBusinessLocations] = useState("");
  const [businessType, setBusinessType] = useState("");

  // Business Opportunity errors
  const [businessTitleError, setBusinessTitleError] = useState("");
  const [natureOfBusinessError, setNatureOfBusinessError] = useState("");
  const [businessLocationsError, setBusinessLocationsError] = useState("");
  const [businessTypeError, setBusinessTypeError] = useState("");
  
  // Technical Consultant fields
  const [consultantId, setConsultantId] = useState("");
  const [consultantTitle, setConsultantTitle] = useState("");
  const [consultantVacancies, setConsultantVacancies] = useState("");
  const [consultantExperience, setConsultantExperience] = useState("");
  const [consultantLocations, setConsultantLocations] = useState("");
  const [consultantType, setConsultantType] = useState("");
  
  // Technical Consultant errors
  const [consultantTitleError, setConsultantTitleError] = useState("");
  const [consultantVacanciesError, setConsultantVacanciesError] = useState("");
  const [consultantExperienceError, setConsultantExperienceError] = useState("");
  const [consultantLocationsError, setConsultantLocationsError] = useState("");
  const [consultantTypeError, setConsultantTypeError] = useState("");

  // Listings state
  const [allListings, setAllListings] = useState({
    job_openings: [],
    business_opportunities: [],
    technical_consultants: []
  });
  const [filteredListings, setFilteredListings] = useState([]);
  const [isLoadingListings, setIsLoadingListings] = useState(true);
  const [listingsError, setListingsError] = useState(null);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);

  // Filter and search states
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPostType, setFilterPostType] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest");
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Generate next ID for new posts
  const generateNextId = useCallback((prefix, listings) => {
    let maxId = 0;
    
    if (prefix === "job") {
      listings.forEach(item => {
        const idNum = item.job_id ? parseInt(item.job_id.split('-')[1]) : 0;
        if (idNum > maxId) maxId = idNum;
      });
      return `job-${String(maxId + 1).padStart(3, '0')}`;
    } 
    else if (prefix === "business") {
      listings.forEach(item => {
        const idNum = item.business_id ? parseInt(item.business_id.split('-')[1]) : 0;
        if (idNum > maxId) maxId = idNum;
      });
      return `business-${String(maxId + 1).padStart(3, '0')}`;
    } 
    else if (prefix === "consultant") {
      listings.forEach(item => {
        const idNum = item.consultant_id ? parseInt(item.consultant_id.split('-')[1]) : 0;
        if (idNum > maxId) maxId = idNum;
      });
      return `consultant-${String(maxId + 1).padStart(3, '0')}`;
    }
    
    return `${prefix}-001`;
  }, []);

  // Reset all error states
  const resetAllErrors = () => {
    setDescriptionError("");
    setJobTitleError(""); setJobVacanciesError(""); setJobExperienceError("");
    setJobLocationError(""); setJobRoleTypeError("");
    setBusinessTitleError(""); setNatureOfBusinessError("");
    setBusinessLocationsError(""); setBusinessTypeError("");
    setConsultantTitleError(""); setConsultantVacanciesError("");
    setConsultantExperienceError(""); setConsultantLocationsError("");
    setConsultantTypeError("");
  };

  // Reset form fields based on current post type
  const resetFormForType = useCallback((type) => {
    setDescription("");
    
    switch(type) {
      case "job":
        setJobTitle("");
        setJobVacancies("");
        setJobExperience("");
        setJobLocation("");
        setJobRoleType("Unknown");
        // Generate ID for new job posting
        const jobId = generateNextId("job", allListings.job_openings);
        setJobId(jobId);
        break;
      case "business":
        setBusinessTitle("");
        setNatureOfBusiness("");
        setBusinessLocations("");
        setBusinessType("");
        const businessId = generateNextId("business", allListings.business_opportunities);
        setBusinessId(businessId);
        break;
      case "consultant":
        setConsultantTitle("");
        setConsultantVacancies("");
        setConsultantExperience("");
        setConsultantLocations("");
        setConsultantType("Contract");
        const consultantId = generateNextId("consultant", allListings.technical_consultants);
        setConsultantId(consultantId);
        break;
    }
    resetAllErrors();
  }, [generateNextId, allListings]);

  // Handle post type change
  const handlePostTypeChange = (e) => {
    const newType = e.target.value;
    setPostType(newType);
    resetFormForType(newType);
    setIsEditing(false);
    setEditingId(null);
  };

  // Fetch all listings
  const fetchListings = useCallback(async () => {
    setIsLoadingListings(true);
    setListingsError(null);
    try {
      const response = await axios.get(`${apiBaseUrl}/jobpostings_list/`);
      const data = response.data;
      
      setAllListings({
        job_openings: data.job_openings || [],
        business_opportunities: data.business_opportunities || [],
        technical_consultants: data.technical_consultants || []
      });
    } catch (error) {
      setListingsError("Failed to load listings.");
      setAllListings({
        job_openings: [],
        business_opportunities: [],
        technical_consultants: []
      });
    } finally {
      setIsLoadingListings(false);
    }
  }, []);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  // Initialize form with job ID when component mounts or when postType is 'job' initially
  useEffect(() => {
    if (postType === "job" && !jobId && allListings.job_openings.length > 0) {
      const newJobId = generateNextId("job", allListings.job_openings);
      setJobId(newJobId);
    }
  }, [postType, jobId, allListings.job_openings, generateNextId]);

  // Flatten and combine all listings
  const getAllListingsArray = () => {
    const jobListings = (allListings.job_openings || []).map(item => ({
      ...item,
      post_type: "job",
      id_field: item.job_id,
      title: item.jobtitle,
      status_field: item.status,
      created_at_field: item.created_at,
      // Fix: Use role_type from API response instead of type
      type: item.role_type || "Unknown"  // Changed from item.type to item.role_type
    }));
    
    const businessListings = (allListings.business_opportunities || []).map(item => ({
      ...item,
      post_type: "business",
      id_field: item.business_id,
      title: item.business_title,
      status_field: item.status,
      created_at_field: item.created_at
    }));
    
    const consultantListings = (allListings.technical_consultants || []).map(item => ({
      ...item,
      post_type: "consultant",
      id_field: item.consultant_id,
      title: item.title,
      status_field: item.status,
      created_at_field: item.created_at,
      type: item.consultant_type || "Contract"  // Using consultant_type for consistency
    }));
    
    return [...jobListings, ...businessListings, ...consultantListings];
  };

  // Apply filters and search
  useEffect(() => {
    let allListingsArray = getAllListingsArray();
    let filtered = [...allListingsArray];
    
    // Filter by post type
    if (filterPostType !== "all") {
      filtered = filtered.filter(item => item.post_type === filterPostType);
    }
    
    // Filter by status
    if (filterStatus !== "all") {
      filtered = filtered.filter(item => item.status_field === filterStatus);
    }
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item => {
        return (
          (item.title || "").toLowerCase().includes(term) ||
          (item.id_field || "").toLowerCase().includes(term) ||
          (item.job_location || "").toLowerCase().includes(term) ||
          (item.locations_type || "").toLowerCase().includes(term) ||
          (item.locations || "").toLowerCase().includes(term) ||
          (item.nature_of_business || "").toLowerCase().includes(term)
        );
      });
    }
    
    // Sort by date
    filtered.sort((a, b) => {
      const dateA = new Date(a.created_at_field || 0);
      const dateB = new Date(b.created_at_field || 0);
      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });
    
    setFilteredListings(filtered);
  }, [allListings, searchTerm, filterStatus, filterPostType, sortOrder]);

  // Validate form fields
  const validateAllFields = () => {
    resetAllErrors();
    let isValid = true;
    
    const commonDescError = !stripHtml(description).trim() ? "Description is required." : "";
    if (commonDescError) {
      setDescriptionError(commonDescError);
      isValid = false;
    }

    switch (postType) {
      case "job":
        if (!jobTitle.trim()) { setJobTitleError("Job Title is required."); isValid = false; }
        if (!jobVacancies || !/^\d+$/.test(jobVacancies) || parseInt(jobVacancies, 10) <= 0) { 
          setJobVacanciesError("Vacancies must be a positive number."); isValid = false; 
        }
        if (!jobExperience.trim()) { setJobExperienceError("Experience is required."); isValid = false; }
        if (!jobLocation.trim()) { setJobLocationError("Job Location is required."); isValid = false; }
        if (!jobRoleType) { setJobRoleTypeError("Please select a role type."); isValid = false; }
        break;
      
      case "business":
        if (!businessTitle.trim()) { setBusinessTitleError("Business Title is required."); isValid = false; }
        if (!natureOfBusiness.trim()) { setNatureOfBusinessError("Nature of Business is required."); isValid = false; }
        if (!businessLocations.trim()) { setBusinessLocationsError("Locations are required."); isValid = false; }
        if (!businessType.trim()) { setBusinessTypeError("Type is required."); isValid = false; }
        break;

      case "consultant":
        if (!consultantTitle.trim()) { setConsultantTitleError("Title is required."); isValid = false; }
        if (!consultantVacancies || !/^\d+$/.test(consultantVacancies) || parseInt(consultantVacancies, 10) <= 0) { 
          setConsultantVacanciesError("Vacancies must be a positive number."); isValid = false; 
        }
        if (!consultantExperience.trim()) { setConsultantExperienceError("Experience is required."); isValid = false; }
        if (!consultantLocations.trim()) { setConsultantLocationsError("Locations are required."); isValid = false; }
        if (!consultantType) { setConsultantTypeError("Please select a type."); isValid = false; }
        break;
      
      default:
        isValid = false;
    }
    return isValid;
  };

  // Handle form submission (Create or Update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateAllFields()) {
      Swal.fire({ title: "Validation Error", text: "Please fix the errors before submitting.", icon: "warning", timer: 3000 });
      return;
    }
    
    setIsLoading(true);

    let payload, endpoint, method;
    
    // Both POST and PATCH use the same endpoint
    endpoint = `${apiBaseUrl}/JobPosting_view/`;
    
    if (isEditing) {
      // UPDATE - PATCH method
      method = "patch";
      
      // For PATCH, use 'id' field in payload (not in URL)
      switch (postType) {
        case "job":
          payload = {
            post_type: "job",
            id: jobId,
            jobtitle: jobTitle,
            vacancies: parseInt(jobVacancies),
            experience: jobExperience,
            job_location: jobLocation,
            role_type: jobRoleType,  // Changed from 'type' to 'role_type'
            description: description,
          };
          break;
        
        case "business":
          payload = {
            post_type: "business",
            id: businessId,
            business_title: businessTitle,
            nature_of_business: natureOfBusiness,
            locations_type: businessLocations,
            type: businessType,
            description: description,
          };
          break;

        case "consultant":
          payload = {
            post_type: "consultant",
            id: consultantId,
            title: consultantTitle,
            vacancies: parseInt(consultantVacancies),
            experience: consultantExperience,
            locations: consultantLocations,
            consultant_type: consultantType,
            description: description,
          };
          break;
      }
    } else {
      // CREATE - POST method
      method = "post";
      
      // For POST, use the original field names
      switch (postType) {
        case "job":
          payload = {
            post_type: "job",
            job_id: jobId,
            jobtitle: jobTitle,
            vacancies: parseInt(jobVacancies),
            experience: jobExperience,
            job_location: jobLocation,
            role_type: jobRoleType,  // Changed from 'type' to 'role_type'
            description: description,
          };
          break;
        
        case "business":
          payload = {
            post_type: "business",
            business_id: businessId,
            business_title: businessTitle,
            nature_of_business: natureOfBusiness,
            locations_type: businessLocations,
            type: businessType,
            description: description,
          };
          break;

        case "consultant":
          payload = {
            post_type: "consultant",
            consultant_id: consultantId,
            title: consultantTitle,
            vacancies: parseInt(consultantVacancies),
            experience: consultantExperience,
            locations: consultantLocations,
            consultant_type: consultantType,
            description: description,
          };
          break;
      }
    }

    try {
      if (method === "post") {
        await axios.post(endpoint, payload);
      } else {
        // PATCH request without ID in URL
        await axios.patch(endpoint, payload);
      }
      
      await Swal.fire({ 
        title: "Success!", 
        text: `Your post has been ${isEditing ? "updated" : "created"} successfully.`, 
        icon: "success", 
        timer: 1500, 
        showConfirmButton: false 
      });
      
      await fetchListings();
      resetFormForType(postType);
      setIsEditing(false);
      setEditingId(null);
      
    } catch (error) {
      console.error("API Error:", error.response || error);
      Swal.fire("Error", `An error occurred while ${isEditing ? "updating" : "posting"}. ${error.response?.data?.message || ''}`, "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle edit listing
  const handleEditListing = (listing) => {
    document.querySelector('.job-form-container')?.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Set post type based on listing
    setPostType(listing.post_type || "job");
    setDescription(listing.description || "");
    setIsEditing(true);
    
    // Set ID for editing
    const id = listing.job_id || listing.business_id || listing.consultant_id;
    setEditingId(id);
    
    // Populate form based on post type
    switch(listing.post_type) {
      case "job":
        setJobId(listing.job_id || "");
        setJobTitle(listing.jobtitle || "");
        setJobVacancies(listing.vacancies?.toString() || "");
        setJobExperience(listing.experience || "");
        setJobLocation(listing.job_location || "");
        // Fix: Use role_type from API response
        setJobRoleType(listing.role_type || "Unknown");
        break;
        
      case "business":
        setBusinessId(listing.business_id || "");
        setBusinessTitle(listing.business_title || "");
        setNatureOfBusiness(listing.nature_of_business || "");
        setBusinessLocations(listing.locations_type || "");
        setBusinessType(listing.type || "");
        break;
        
      case "consultant":
        setConsultantId(listing.consultant_id || "");
        setConsultantTitle(listing.title || "");
        setConsultantVacancies(listing.vacancies?.toString() || "");
        setConsultantExperience(listing.experience || "");
        setConsultantLocations(listing.locations || "");
        setConsultantType(listing.consultant_type || "Contract");
        break;
    }
    
    resetAllErrors();
  };

  // Handle status toggle
// Handle status toggle
const handleToggleStatus = async (listing) => {
  const targetStatus = listing.status_field === "Active" ? "Inactive" : "Active";
  const id = listing.job_id || listing.business_id || listing.consultant_id;
  
  // Confirm the action
  const result = await Swal.fire({
    title: 'Are you sure?',
    text: `Do you want to ${targetStatus === "Active" ? "activate" : "deactivate"} this listing?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    reverseButtons: true
  });

  if (!result.isConfirmed) return;

  setUpdatingStatusId(id);
  
  try {
    // Determine the endpoint based on listing type
    let endpoint = '';
    let payload = { status: targetStatus };
    
    if (listing.post_type === 'job') {
      endpoint = `${apiBaseUrl}/update_job_posting/${id}/`;
    } else if (listing.post_type === 'business') {
      // You might need a separate endpoint for business opportunities
      endpoint = `${apiBaseUrl}/update_business_opportunity/${id}/`;
    } else if (listing.post_type === 'consultant') {
      // You might need a separate endpoint for technical consultants
      endpoint = `${apiBaseUrl}/update_technical_consultant/${id}/`;
    }

    // If endpoint is not available, use a generic one or show error
    if (!endpoint) {
      Swal.fire("Error", "API endpoint not configured for this listing type.", "error");
      return;
    }

    // Make PUT request to update status
    await axios.put(endpoint, payload);
    
    await Swal.fire({ 
      title: "Status Updated!", 
      text: `Listing is now ${targetStatus}.`, 
      icon: "success", 
      timer: 1500, 
      showConfirmButton: false 
    });
    
    // Refresh the listings
    await fetchListings();
    
  } catch (error) {
    console.error("Status update error:", error);
    
    // Show detailed error message
    let errorMessage = "Failed to update status.";
    if (error.response) {
      errorMessage = error.response.data.message || errorMessage;
    } else if (error.request) {
      errorMessage = "No response from server. Please check your connection.";
    }
    
    Swal.fire("Update Error", errorMessage, "error");
  } finally {
    setUpdatingStatusId(null);
  }
};

  // Cancel edit
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingId(null);
    resetFormForType(postType);
  };

  // Get title based on post type
  const getListingTitle = (listing) => {
    return listing.title || 'Untitled';
  };

  // Get ID based on post type
  const getListingId = (listing) => {
    return listing.id_field || 'N/A';
  };

  // Render form based on post type
  const renderForm = () => {
    switch(postType) {
      case "job":
        return renderJobOpeningForm();
      case "business":
        return renderBusinessOpportunityForm();
      case "consultant":
        return renderTechnicalConsultantForm();
      default:
        return null;
    }
  };

  const renderJobOpeningForm = () => (
    <>
      <div className="mb-3">
        <label className="form-label">Job ID</label>
        <input type="text" className="form-control" value={jobId || generateNextId("job", allListings.job_openings)} readOnly style={{ backgroundColor: '#e9ecef' }} />
      </div>
      <div className="mb-3">
        <label className="form-label">Job Title</label>
        <input type="text" className={`form-control ${jobTitleError ? 'is-invalid' : ''}`} placeholder="Enter title" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} />
        {jobTitleError && <div className="invalid-feedback">{jobTitleError}</div>}
      </div>
      <div className="row mb-3">
        <div className="col-md-6">
          <label className="form-label">Vacancies</label>
          <input type="number" min="1" className={`form-control ${jobVacanciesError ? 'is-invalid' : ''}`} placeholder="Count" value={jobVacancies} onChange={(e) => setJobVacancies(e.target.value)} />
          {jobVacanciesError && <div className="invalid-feedback">{jobVacanciesError}</div>}
        </div>
        <div className="col-md-6">
          <label className="form-label">Experience</label>
          <input type="text" className={`form-control ${jobExperienceError ? 'is-invalid' : ''}`} placeholder="e.g., 2+ years" value={jobExperience} onChange={(e) => setJobExperience(e.target.value)} />
          {jobExperienceError && <div className="invalid-feedback">{jobExperienceError}</div>}
        </div>
      </div>
      <div className="row mb-3">
        <div className="col-md-6">
          <label className="form-label">Job Location</label>
          <input type="text" className={`form-control ${jobLocationError ? 'is-invalid' : ''}`} placeholder="Locations" value={jobLocation} onChange={(e) => setJobLocation(e.target.value)} />
          {jobLocationError && <div className="invalid-feedback">{jobLocationError}</div>}
        </div>
        <div className="col-md-6">
          <label className="form-label">Role Type</label>
          <select className={`form-select ${jobRoleTypeError ? 'is-invalid' : ''}`} value={jobRoleType} onChange={(e) => setJobRoleType(e.target.value)}>
            <option value="" disabled>Choose...</option>
            <option value="Full Time">Full Time</option>
            <option value="Part Time">Part Time</option>
            <option value="Contract">Contract</option>
            <option value="Intern">Intern</option>
            <option value="Unknown">Unknown</option>
          </select>
          {jobRoleTypeError && <div className="invalid-feedback">{jobRoleTypeError}</div>}
        </div>
      </div>
    </>
  );

  const renderBusinessOpportunityForm = () => (
    <>
      <div className="mb-3">
        <label className="form-label">Business ID</label>
        <input type="text" className="form-control" value={businessId || generateNextId("business", allListings.business_opportunities)} readOnly style={{ backgroundColor: '#e9ecef' }} />
      </div>
      <div className="mb-3">
        <label className="form-label">Business Title</label>
        <input type="text" className={`form-control ${businessTitleError ? 'is-invalid' : ''}`} value={businessTitle} onChange={(e) => setBusinessTitle(e.target.value)} />
        {businessTitleError && <div className="invalid-feedback">{businessTitleError}</div>}
      </div>
      <div className="mb-3">
        <label className="form-label">Nature of Business</label>
        <input type="text" className={`form-control ${natureOfBusinessError ? 'is-invalid' : ''}`} value={natureOfBusiness} onChange={(e) => setNatureOfBusiness(e.target.value)} />
        {natureOfBusinessError && <div className="invalid-feedback">{natureOfBusinessError}</div>}
      </div>
      <div className="row mb-3">
        <div className="col-md-6">
          <label className="form-label">Locations</label>
          <input type="text" className={`form-control ${businessLocationsError ? 'is-invalid' : ''}`} value={businessLocations} onChange={(e) => setBusinessLocations(e.target.value)} />
          {businessLocationsError && <div className="invalid-feedback">{businessLocationsError}</div>}
        </div>
        <div className="col-md-6">
          <label className="form-label">Type</label>
          <input type="text" className={`form-control ${businessTypeError ? 'is-invalid' : ''}`} value={businessType} onChange={(e) => setBusinessType(e.target.value)} />
          {businessTypeError && <div className="invalid-feedback">{businessTypeError}</div>}
        </div>
      </div>
    </>
  );

  const renderTechnicalConsultantForm = () => (
    <>
      <div className="mb-3">
        <label className="form-label">Technical ID</label>
        <input type="text" className="form-control" value={consultantId || generateNextId("consultant", allListings.technical_consultants)} readOnly style={{ backgroundColor: '#e9ecef' }} />
      </div>
      <div className="mb-3">
        <label className="form-label">Technical Title</label>
        <input type="text" className={`form-control ${consultantTitleError ? 'is-invalid' : ''}`} value={consultantTitle} onChange={(e) => setConsultantTitle(e.target.value)} />
        {consultantTitleError && <div className="invalid-feedback">{consultantTitleError}</div>}
      </div>
      <div className="row mb-3">
        <div className="col-md-6">
          <label className="form-label">Vacancies</label>
          <input type="number" min="1" className={`form-control ${consultantVacanciesError ? 'is-invalid' : ''}`} value={consultantVacancies} onChange={(e) => setConsultantVacancies(e.target.value)} />
          {consultantVacanciesError && <div className="invalid-feedback">{consultantVacanciesError}</div>}
        </div>
        <div className="col-md-6">
          <label className="form-label">Experience</label>
          <input type="text" className={`form-control ${consultantExperienceError ? 'is-invalid' : ''}`} value={consultantExperience} onChange={(e) => setConsultantExperience(e.target.value)} />
          {consultantExperienceError && <div className="invalid-feedback">{consultantExperienceError}</div>}
        </div>
      </div>
      <div className="row mb-3">
        <div className="col-md-6">
          <label className="form-label">Locations</label>
          <input type="text" className={`form-control ${consultantLocationsError ? 'is-invalid' : ''}`} value={consultantLocations} onChange={(e) => setConsultantLocations(e.target.value)} />
          {consultantLocationsError && <div className="invalid-feedback">{consultantLocationsError}</div>}
        </div>
        <div className="col-md-6">
          <label className="form-label">Type</label>
          <select className={`form-select ${consultantTypeError ? 'is-invalid' : ''}`} value={consultantType} onChange={(e) => setConsultantType(e.target.value)}>
            <option value="" disabled>Choose...</option>
            <option value="Contract">Contract</option>
            <option value="Project-Based">Project-Based</option>
            <option value="Full-Time">Full-Time</option>
          </select>
          {consultantTypeError && <div className="invalid-feedback">{consultantTypeError}</div>}
        </div>
      </div>
    </>
  );

  // Mobile filter toggle
  const MobileFilterPanel = () => (
    <div className={`mobile-filters-panel ${showMobileFilters ? 'show' : ''}`}>
      <div className="mobile-filters-header">
        <h6>Filters</h6>
        <button className="btn-close" onClick={() => setShowMobileFilters(false)}></button>
      </div>
      <div className="mobile-filters-content">
        <div className="mb-3">
          <label className="form-label">Search</label>
          <input
            type="text"
            className="form-control"
            placeholder="Search by title or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Post Type</label>
          <select
            className="form-select"
            value={filterPostType}
            onChange={(e) => setFilterPostType(e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="job">Job Openings</option>
            <option value="business">Business Opportunities</option>
            <option value="consultant">Technical Consultants</option>
          </select>
        </div>
        <div className="mb-3">
          <label className="form-label">Status</label>
          <select
            className="form-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
        <div className="mb-3">
          <label className="form-label">Sort By</label>
          <select
            className="form-select"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </select>
        </div>
        <button 
          className="btn btn-primary w-100"
          onClick={() => setShowMobileFilters(false)}
        >
          Apply Filters
        </button>
      </div>
    </div>
  );

  return (
    <div>
      <style>
        {`
          .ql-font-arial { font-family: "Arial", sans-serif; }
          .ql-font-georgia { font-family: "Georgia", serif; }
          .ql-font-comic-sans-ms { font-family: "Comic Sans MS", cursive, sans-serif; }
          .ql-font-courier-new { font-family: "Courier New", monospace; }
          .ql-font-helvetica { font-family: "Helvetica", sans-serif; }
          .ql-font-lucida { font-family: "Lucida Sans Unicode", sans-serif; }
          .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="arial"]::before,
          .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="arial"]::before { content: 'Arial'; font-family: "Arial", sans-serif; }
          .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="georgia"]::before,
          .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="georgia"]::before { content: 'Georgia'; font-family: "Georgia", serif; }
          .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="comic-sans-ms"]::before,
          .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="comic-sans-ms"]::before { content: 'Comic Sans'; font-family: "Comic Sans MS", cursive, sans-serif; }
          .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="courier-new"]::before,
          .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="courier-new"]::before { content: 'Courier New'; font-family: "Courier New", monospace; }
          .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="helvetica"]::before,
          .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="helvetica"]::before { content: 'Helvetica'; font-family: "Helvetica", sans-serif; }
          .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="lucida"]::before,
          .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="lucida"]::before { content: 'Lucida'; font-family: "Lucida Sans Unicode", sans-serif; }
        `}
      </style>
      <DashNavbar />
      <div className="wrapper">
        <Sidebar />
        <main className="main">
          <div className="job-posting-page-wrapper">
            {/* Mobile Filters Overlay */}
            {showMobileFilters && (
              <div className="mobile-filters-overlay" onClick={() => setShowMobileFilters(false)}></div>
            )}
            
            {/* Form Container */}
            <div className="job-form-container">
              <div className="job-posting-form-inner">
                <h2 className="gradient-text mb-4 text-center">
                  {isEditing ? "Edit Posting" : "Posting Form"}
                </h2>
                <form onSubmit={handleSubmit} noValidate>
                  <div className="mb-3">
                    <label className="form-label">Post As</label>
                    <select 
                      className="form-select" 
                      value={postType} 
                      onChange={handlePostTypeChange}
                      disabled={isEditing}
                    >
                      <option value="job">Job Opening</option>
                      <option value="business">Business Opportunity</option>
                      <option value="consultant">Technical Consultant</option>
                    </select>
                  </div>
                  <hr/>
                  {renderForm()}
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <ReactQuill 
                      theme="snow" 
                      value={description} 
                      onChange={setDescription} 
                      modules={quillModules} 
                      formats={quillFormats} 
                      placeholder="Details..." 
                    />
                    {descriptionError && <div className="text-danger small mt-1">{descriptionError}</div>}
                  </div>
                  <div className="pt-3 d-grid gap-2">
                    <button type="submit" className="btn btn-primary btn-lg" disabled={isLoading}>
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          {isEditing ? "Updating..." : "Posting..."}
                        </>
                      ) : isEditing ? "Update" : "Post"}
                    </button>
                    {isEditing && (
                      <button 
                        type="button" 
                        className="btn btn-secondary btn-lg" 
                        onClick={handleCancelEdit} 
                        disabled={isLoading}
                      >
                        Cancel Edit
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {/* Listings Container with Filters */}
            <div className="job-list-container">
              <div className="listings-header mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="text-secondary mb-0">Posted Listings</h5>
                  <div className="d-flex gap-2">
                    <button 
                      className="btn btn-sm btn-outline-secondary d-md-none"
                      onClick={() => setShowMobileFilters(!showMobileFilters)}
                    >
                      <i className="fas fa-filter me-1"></i> Filters
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-secondary"
                      onClick={fetchListings}
                      disabled={isLoadingListings}
                    >
                      <i className="fas fa-sync-alt"></i>
                      <span className="d-none d-md-inline ms-1">Refresh</span>
                    </button>
                  </div>
                </div>
                
                {/* Desktop Filters */}
                <div className="d-none d-md-block">
                  <div className="row g-3 mb-3">
                    <div className="col-md-4">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by title or ID..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                    <div className="col-md-3">
                      <select
                        className="form-select"
                        value={filterPostType}
                        onChange={(e) => setFilterPostType(e.target.value)}
                      >
                        <option value="all">All Types</option>
                        <option value="job">Job Openings</option>
                        <option value="business">Business Opportunities</option>
                        <option value="consultant">Technical Consultants</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <select
                        className="form-select"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                      >
                        <option value="all">All Status</option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    </div>
                    <div className="col-md-2">
                      <select
                        className="form-select"
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value)}
                      >
                        <option value="newest">Newest First</option>
                        <option value="oldest">Oldest First</option>
                      </select>
                    </div>
                  </div>
                </div>
                
                {/* Stats */}
                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    Showing {filteredListings.length} of {getAllListingsArray().length} listings
                  </small>
                  {(searchTerm || filterStatus !== "all" || filterPostType !== "all") && (
                    <button 
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        setSearchTerm("");
                        setFilterStatus("all");
                        setFilterPostType("all");
                      }}
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              </div>

              {/* Listings List */}
              {isLoadingListings ? (
                <div className="text-center mt-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading listings...</p>
                </div>
              ) : listingsError ? (
                <div className="alert alert-danger">{listingsError}</div>
              ) : filteredListings.length === 0 ? (
                <div className="text-center mt-5">
                  <p className="text-muted">No listings found.</p>
                  {(searchTerm || filterStatus !== "all" || filterPostType !== "all") ? (
                    <button 
                      className="btn btn-outline-primary btn-sm mt-2"
                      onClick={() => {
                        setSearchTerm("");
                        setFilterStatus("all");
                        setFilterPostType("all");
                      }}
                    >
                      Clear Filters
                    </button>
                  ) : (
                    <p className="text-muted">Create your first listing using the form!</p>
                  )}
                </div>
              ) : (
                <div className="listings-list">
                  {filteredListings.map((listing, index) => {
                    const isUpdating = updatingStatusId === getListingId(listing);
                    const isCurrentlyEditing = isEditing && editingId === getListingId(listing);
                    
                    return (
                      <div key={`${listing.post_type}-${listing.id_field || index}`} className={`job-list-card ${isCurrentlyEditing ? 'editing' : ''}`}>
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center mb-2 flex-wrap">
                              <span className={`post-type-badge ${listing.post_type || 'job'}`}>
                                {listing.post_type === 'job' ? 'Job' : 
                                 listing.post_type === 'business' ? 'Business' : 'Consultant'}
                              </span>
                              <h6 className="card-title mb-0 ms-2">{getListingTitle(listing)}</h6>
                            </div>
                            <p className="job-id mb-2">
                              <small>ID: {getListingId(listing)}</small>
                            </p>
                            <div className="listing-details mb-2">
                              <small className="text-muted">
                                {listing.post_type === 'job' && (
                                  <>
                                    {listing.job_location && `📍 ${listing.job_location}`}
                                    {listing.type && ` • ${listing.type}`}  {/* Now shows role_type correctly */}
                                    {listing.vacancies && ` • 👥 ${listing.vacancies}`}
                                  </>
                                )}
                                {listing.post_type === 'business' && (
                                  <>
                                    {listing.locations_type && `📍 ${listing.locations_type}`}
                                    {listing.nature_of_business && ` • ${listing.nature_of_business.substring(0, 50)}${listing.nature_of_business.length > 50 ? '...' : ''}`}
                                  </>
                                )}
                                {listing.post_type === 'consultant' && (
                                  <>
                                    {listing.locations && `📍 ${listing.locations}`}
                                    {listing.type && ` • ${listing.type}`}
                                    {listing.vacancies && ` • 👥 ${listing.vacancies}`}
                                  </>
                                )}
                              </small>
                            </div>
                          </div>
                          <span className={`job-status ${listing.status_field === 'Active' ? 'active' : 'inactive'}`}>
                            {listing.status_field || 'N/A'}
                          </span>
                        </div>
                        
                        <div className="action-buttons mt-3">
                          <button 
                            className="btn btn-edit btn-sm" 
                            onClick={() => handleEditListing(listing)}
                            disabled={isLoading || isUpdating || isCurrentlyEditing}
                            title="Edit"
                          >
                            <i className="fas fa-edit me-1"></i> 
                            <span className="d-none d-md-inline">Edit</span>
                          </button>
                          <button 
                            className={`btn btn-sm ${listing.status_field === 'Active' ? 'btn-outline-danger' : 'btn-outline-success'}`}
                            onClick={() => handleToggleStatus(listing)}
                            disabled={isUpdating || isLoading || isCurrentlyEditing}
                          >
                            {isUpdating ? (
                              <span className="spinner-border spinner-border-sm"></span>
                            ) : listing.status_field === 'Active' ? (
                              <>
                                <i className="fas fa-ban me-1"></i> 
                                <span className="d-none d-md-inline">Deactivate</span>
                              </>
                            ) : (
                              <>
                                <i className="fas fa-check me-1"></i> 
                                <span className="d-none d-md-inline">Activate</span>
                              </>
                            )}
                          </button>
                        </div>
                        
                        <div className="d-flex justify-content-between align-items-center mt-2">
                          <small className="text-muted">
                            Posted: {listing.created_at_field ? new Date(listing.created_at_field).toLocaleDateString() : 'N/A'}
                          </small>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          {/* Mobile Filters Panel */}
          <MobileFilterPanel />
        </main>
      </div>
    </div>
  );
};

export default Jobposting;

// import React, { useState, useEffect, useCallback } from "react";
// import axios from "axios";
// import Swal from "sweetalert2";
// import Sidebar from "./Sidebar";
// import DashNavbar from "./DashNavbar";
// import ReactQuill, { Quill } from 'react-quill';
// import 'react-quill/dist/quill.snow.css';
// import '../css/JobPosting.css';

// // --- Quill Font & Style Configuration ---
// const Font = Quill.import('formats/font');
// Font.whitelist = ['arial', 'georgia', 'comic-sans-ms', 'courier-new', 'helvetica', 'lucida'];
// Quill.register(Font, true);

// const stripHtml = (html) => {
//   if (!html || html === '<p><br></p>' || html === '<p></p>') return "";
//   const doc = new DOMParser().parseFromString(html, 'text/html');
//   return doc.body.textContent || "";
// };

// const quillModules = {
//   toolbar: [
//     [{ 'header': [1, 2, 3, 4, 5, 6, false] }, { 'font': Font.whitelist }],
//     ['bold', 'italic', 'underline', 'strike'],
//     [{ 'list': 'ordered'}, { 'list': 'bullet' }], [{ 'indent': '-1'}, { 'indent': '+1' }],
//     ['link'], ['clean']
//   ],
// };

// const quillFormats = [
//   'header', 'font', 'bold', 'italic', 'underline', 'strike', 
//   'list', 'bullet', 'indent', 'link'
// ];

// const apiBaseUrl = "https://raasbackend.vetrinahealthcare.com";

// const Jobposting = () => {
//   // Control State
//   const [postType, setPostType] = useState("job"); // 'job', 'business', or 'consultant'
//   const [isLoading, setIsLoading] = useState(false);
//   const [isEditing, setIsEditing] = useState(false); // Note: Editing logic is simplified to Job Openings as per original code
//   const [editingJobId, setEditingJobId] = useState(null);

//   // Common State
//   const [description, setDescription] = useState("");
//   const [descriptionError, setDescriptionError] = useState("");

//   // Job Opening State
//   const [jobId, setJobId] = useState("JOB-1");
//   const [jobTitle, setJobTitle] = useState("");
//   const [jobVacancies, setJobVacancies] = useState("");
//   const [jobExperience, setJobExperience] = useState("");
//   const [jobLocation, setJobLocation] = useState("");
//   const [jobRoleType, setJobRoleType] = useState("");
  
//   // Job Opening Error State
//   const [jobTitleError, setJobTitleError] = useState("");
//   const [jobVacanciesError, setJobVacanciesError] = useState("");
//   const [jobExperienceError, setJobExperienceError] = useState("");
//   const [jobLocationError, setJobLocationError] = useState("");
//   const [jobRoleTypeError, setJobRoleTypeError] = useState("");

//   // Business Opportunity State
//   const [businessId, setBusinessId] = useState("BUC-1");
//   const [businessTitle, setBusinessTitle] = useState("");
//   const [natureOfBusiness, setNatureOfBusiness] = useState("");
//   const [businessLocations, setBusinessLocations] = useState("");
//   const [businessType, setBusinessType] = useState("");

//   // Business Opportunity Error State
//   const [businessTitleError, setBusinessTitleError] = useState("");
//   const [natureOfBusinessError, setNatureOfBusinessError] = useState("");
//   const [businessLocationsError, setBusinessLocationsError] = useState("");
//   const [businessTypeError, setBusinessTypeError] = useState("");
  
//   // Technical Consultant State
//   const [consultantId, setConsultantId] = useState("TEC-1");
//   const [consultantTitle, setConsultantTitle] = useState("");
//   const [consultantVacancies, setConsultantVacancies] = useState("");
//   const [consultantExperience, setConsultantExperience] = useState("");
//   const [consultantLocations, setConsultantLocations] = useState("");
//   const [consultantType, setConsultantType] = useState("");
  
//   // Technical Consultant Error State
//   const [consultantTitleError, setConsultantTitleError] = useState("");
//   const [consultantVacanciesError, setConsultantVacanciesError] = useState("");
//   const [consultantExperienceError, setConsultantExperienceError] = useState("");
//   const [consultantLocationsError, setConsultantLocationsError] = useState("");
//   const [consultantTypeError, setConsultantTypeError] = useState("");

//   // Posted Jobs List State
//   const [postedJobs, setPostedJobs] = useState([]);
//   const [isLoadingPostedJobs, setIsLoadingPostedJobs] = useState(true);
//   const [postedJobsError, setPostedJobsError] = useState(null);
//   const [updatingStatusJobId, setUpdatingStatusJobId] = useState(null);

//   // --- MOCK ID GENERATION - Replace with API call if available ---
//   const generateNextId = (prefix, lastId) => {
//     const lastNum = lastId ? parseInt(lastId.split('-')[1], 10) : 0;
//     return `${prefix}-${(lastNum || 0) + 1}`;
//   };

//   const resetAllErrors = () => {
//     setDescriptionError("");
//     setJobTitleError(""); setJobVacanciesError(""); setJobExperienceError("");
//     setJobLocationError(""); setJobRoleTypeError("");
//     setBusinessTitleError(""); setNatureOfBusinessError("");
//     setBusinessLocationsError(""); setBusinessTypeError("");
//     setConsultantTitleError(""); setConsultantVacanciesError("");
//     setConsultantExperienceError(""); setConsultantLocationsError("");
//     setConsultantTypeError("");
//   };

//   const resetAllForms = useCallback(() => {
//     setDescription("");
//     // Reset job form
//     setJobTitle(""); setJobVacancies(""); setJobExperience("");
//     setJobLocation(""); setJobRoleType("");
//     // Reset business form
//     setBusinessTitle(""); setNatureOfBusiness(""); setBusinessLocations(""); setBusinessType("");
//     // Reset consultant form
//     setConsultantTitle(""); setConsultantVacancies(""); setConsultantExperience("");
//     setConsultantLocations(""); setConsultantType("");
    
//     // Reset IDs (this should ideally fetch the last ID from the backend)
//     setJobId(generateNextId("JOB", "")); // Placeholder
//     setBusinessId(generateNextId("BUC", "")); // Placeholder
//     setConsultantId(generateNextId("TEC", "")); // Placeholder

//     resetAllErrors();
//   }, []);

//   const handlePostTypeChange = (e) => {
//     const newType = e.target.value;
//     setPostType(newType);
//     resetAllForms();
//     setIsEditing(false);
//     setEditingJobId(null);
//   };

//   const fetchPostedJobs = useCallback(async () => {
//     setIsLoadingPostedJobs(true);
//     setPostedJobsError(null);
//     try {
//       // Assuming this endpoint returns all types of postings
//       const response = await axios.get(`${apiBaseUrl}/jobpostings_list/`);
//       setPostedJobs(response.data || []);
//     } catch (error) {
//       setPostedJobsError("Failed to load postings.");
//     } finally {
//       setIsLoadingPostedJobs(false);
//     }
//   }, []);

//   useEffect(() => {
//     fetchPostedJobs();
//   }, [fetchPostedJobs]);
  
//   const validateAllFields = () => {
//     resetAllErrors();
//     let isValid = true;
//     const commonDescError = !stripHtml(description).trim() ? "Description is required." : "";
//     if (commonDescError) {
//         setDescriptionError(commonDescError);
//         isValid = false;
//     }

//     switch (postType) {
//       case "job":
//         if (!jobTitle.trim()) { setJobTitleError("Job Title is required."); isValid = false; }
//         if (!jobVacancies || !/^\d+$/.test(jobVacancies) || parseInt(jobVacancies, 10) <= 0) { setJobVacanciesError("Vacancies must be a positive number."); isValid = false; }
//         if (!jobExperience.trim()) { setJobExperienceError("Experience is required."); isValid = false; }
//         if (!jobLocation.trim()) { setJobLocationError("Job Location is required."); isValid = false; }
//         if (!jobRoleType) { setJobRoleTypeError("Please select a role type."); isValid = false; }
//         break;
      
//       case "business":
//         if (!businessTitle.trim()) { setBusinessTitleError("B.Title is required."); isValid = false; }
//         if (!natureOfBusiness.trim()) { setNatureOfBusinessError("Nature of Business is required."); isValid = false; }
//         if (!businessLocations.trim()) { setBusinessLocationsError("Locations are required."); isValid = false; }
//         if (!businessType.trim()) { setBusinessTypeError("Type is required."); isValid = false; }
//         break;

//       case "consultant":
//         if (!consultantTitle.trim()) { setConsultantTitleError("Title is required."); isValid = false; }
//         if (!consultantVacancies || !/^\d+$/.test(consultantVacancies) || parseInt(consultantVacancies, 10) <= 0) { setConsultantVacanciesError("Vacancies must be a positive number."); isValid = false; }
//         if (!consultantExperience.trim()) { setConsultantExperienceError("Experience is required."); isValid = false; }
//         if (!consultantLocations.trim()) { setConsultantLocationsError("Locations are required."); isValid = false; }
//         if (!consultantType) { setConsultantTypeError("Please select a type."); isValid = false; }
//         break;
      
//       default:
//         isValid = false;
//     }
//     return isValid;
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!validateAllFields()) {
//       Swal.fire({ title: "Validation Error", text: "Please fix the errors before submitting.", icon: "warning", timer: 3000 });
//       return;
//     }
//     setIsLoading(true);

//     let payload;
//     // NOTE: ID generation is happening client-side as a fallback.
//     // The backend should ideally handle this.
//     switch (postType) {
//       case "job":
//         payload = {
//           post_type: "job",
//           job_id: jobId,
//           jobtitle: jobTitle,
//           vacancies: parseInt(jobVacancies),
//           experience: jobExperience,
//           job_location: jobLocation,
//           role_type: jobRoleType,
//           description: description,
//         };
//         break;
      
//       case "business":
//         payload = {
//           post_type: "business",
//           business_id: businessId, // Assuming API accepts this field
//           business_title: businessTitle,
//           nature_of_business: natureOfBusiness,
//           locations_type: businessLocations,
//           type: businessType, // Assuming API accepts this field
//           description: description,
//         };
//         break;

//       case "consultant":
//         payload = {
//           post_type: "consultant",
//           consultant_id: consultantId, // Assuming API accepts this field
//           title: consultantTitle,
//           vacancies: parseInt(consultantVacancies),
//           experience: consultantExperience,
//           locations: consultantLocations,
//           consultant_type: consultantType,
//           description: description,
//         };
//         break;
      
//       default:
//         Swal.fire("Error", "Invalid post type selected.", "error");
//         setIsLoading(false);
//         return;
//     }

//     try {
//       await axios.post(`${apiBaseUrl}/JobPosting_view/`, payload);
//       await Swal.fire({ title: "Success!", text: "Your post has been created successfully.", icon: "success", timer: 1500, showConfirmButton: false });
//       fetchPostedJobs();
//       resetAllForms();
//       // After submission, reset to default post type
//       setPostType("job"); 
//     } catch (error) {
//       Swal.fire("Error", `An error occurred while posting.`, "error");
//     } finally {
//       setIsLoading(false);
//     }
//   };
  
//   // Simplified handleEdit and handleToggle to work only with job postings as per original structure
//   const handleEditJob = async (jobIdToEdit) => {
//     document.querySelector('.job-form-container')?.scrollTo({ top: 0, behavior: 'smooth' });
//     setIsLoading(true);
//     try {
//       // This assumes the get endpoint only returns job types
//       const response = await axios.get(`${apiBaseUrl}/get_job_posting/${jobIdToEdit}/`);
//       const jobData = response.data;
//       if (jobData) {
//         setPostType("job"); // Switch form to Job Opening
//         resetAllForms(); // Clear other forms

//         setIsEditing(true); 
//         setEditingJobId(jobData.job_id); 
//         setJobId(jobData.job_id);
//         setJobTitle(jobData.jobtitle || ""); 
//         setDescription(jobData.description || "");
//         setJobVacancies(jobData.vacancies?.toString() || ""); 
//         setJobExperience(jobData.experience || "");
//         setJobLocation(jobData.job_location || ""); 
//         setJobRoleType(jobData.role_type || "");
//       } else {
//         Swal.fire("Error", "Could not load job details.", "error");
//       }
//     } catch (error) {
//       Swal.fire("Load Error", "Failed to load job details for editing.", "error");
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleToggleJobStatus = async (job) => {
//     const targetStatus = job.status === "Active" ? "Inactive" : "Active";
//     const action = targetStatus === "Active" ? "activate" : "deactivate";
//     setUpdatingStatusJobId(job.job_id);
//     try {
//       // This assumes status toggling is only for jobs
//       await axios.put(`${apiBaseUrl}/jobpostings/${job.job_id}/${action}/`);
//       Swal.fire({ title: "Status Updated!", text: `Job ${job.job_id} is now ${targetStatus}.`, icon: "success", timer: 1500, showConfirmButton: false });
//       fetchPostedJobs();
//     } catch (error) {
//       Swal.fire("Update Error", `Failed to ${action} job.`, "error");
//     } finally {
//       setUpdatingStatusJobId(null);
//     }
//   };


//   const renderJobOpeningForm = () => (
//     <>
//       <div className="mb-3">
//         <label className="form-label">Job ID</label>
//         <input type="text" className="form-control" value={jobId} readOnly style={{ backgroundColor: '#e9ecef' }} />
//       </div>
//       <div className="mb-3">
//         <label className="form-label">Job Title</label>
//         <input type="text" className={`form-control ${jobTitleError ? 'is-invalid' : ''}`} placeholder="Enter role (e.g., Software Engineer)" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} />
//         {jobTitleError && <div className="invalid-feedback">{jobTitleError}</div>}
//       </div>
//       <div className="row mb-3">
//         <div className="col-md-6">
//           <label className="form-label">Vacancies</label>
//           <input type="text" className={`form-control ${jobVacanciesError ? 'is-invalid' : ''}`} placeholder="Number of vacancies" value={jobVacancies} onChange={(e) => setJobVacancies(e.target.value)} />
//           {jobVacanciesError && <div className="invalid-feedback">{jobVacanciesError}</div>}
//         </div>
//         <div className="col-md-6">
//           <label className="form-label">Experience</label>
//           <input type="text" className={`form-control ${jobExperienceError ? 'is-invalid' : ''}`} placeholder="e.g., 0-1 years, 2+ years" value={jobExperience} onChange={(e) => setJobExperience(e.target.value)} />
//           {jobExperienceError && <div className="invalid-feedback">{jobExperienceError}</div>}
//         </div>
//       </div>
//       <div className="row mb-3">
//         <div className="col-md-6">
//           <label className="form-label">Job Location</label>
//           <input type="text" className={`form-control ${jobLocationError ? 'is-invalid' : ''}`} placeholder="Locations, comma-separated" value={jobLocation} onChange={(e) => setJobLocation(e.target.value)} />
//           {jobLocationError && <div className="invalid-feedback">{jobLocationError}</div>}
//         </div>
//         <div className="col-md-6">
//           <label className="form-label">Role Type</label>
//           <select className={`form-select ${jobRoleTypeError ? 'is-invalid' : ''}`} value={jobRoleType} onChange={(e) => setJobRoleType(e.target.value)}>
//             <option value="" disabled>Choose role type...</option>
//             <option value="Full Time">Full Time</option>
//             <option value="Part Time">Part Time</option>
//             <option value="Contract">Contract</option>
//             <option value="Intern">Intern</option>
//           </select>
//           {jobRoleTypeError && <div className="invalid-feedback">{jobRoleTypeError}</div>}
//         </div>
//       </div>
//     </>
//   );

//   const renderBusinessOpportunityForm = () => (
//     <>
//       <div className="mb-3">
//         <label className="form-label">Bussiness ID</label>
//         <input type="text" className="form-control" value={businessId} readOnly style={{ backgroundColor: '#e9ecef' }} />
//       </div>
//       <div className="mb-3">
//         <label className="form-label">Bussiness Title</label>
//         <input type="text" className={`form-control ${businessTitleError ? 'is-invalid' : ''}`} placeholder="Enter business title" value={businessTitle} onChange={(e) => setBusinessTitle(e.target.value)} />
//         {businessTitleError && <div className="invalid-feedback">{businessTitleError}</div>}
//       </div>
//       <div className="mb-3">
//         <label className="form-label">Nature of Business</label>
//         <input type="text" className={`form-control ${natureOfBusinessError ? 'is-invalid' : ''}`} placeholder="e.g., Distribution, Franchise" value={natureOfBusiness} onChange={(e) => setNatureOfBusiness(e.target.value)} />
//         {natureOfBusinessError && <div className="invalid-feedback">{natureOfBusinessError}</div>}
//       </div>
//       <div className="row mb-3">
//         <div className="col-md-6">
//           <label className="form-label">Locations</label>
//           <input type="text" className={`form-control ${businessLocationsError ? 'is-invalid' : ''}`} placeholder="Locations, comma-separated" value={businessLocations} onChange={(e) => setBusinessLocations(e.target.value)} />
//           {businessLocationsError && <div className="invalid-feedback">{businessLocationsError}</div>}
//         </div>
//         <div className="col-md-6">
//           <label className="form-label">Type</label>
//           <input type="text" className={`form-control ${businessTypeError ? 'is-invalid' : ''}`} placeholder="Enter business type" value={businessType} onChange={(e) => setBusinessType(e.target.value)} />
//           {businessTypeError && <div className="invalid-feedback">{businessTypeError}</div>}
//         </div>
//       </div>
//     </>
//   );

//   const renderTechnicalConsultantForm = () => (
//     <>
//       <div className="mb-3">
//         <label className="form-label">Technical ID</label>
//         <input type="text" className="form-control" value={consultantId} readOnly style={{ backgroundColor: '#e9ecef' }} />
//       </div>
//       <div className="mb-3">
//         <label className="form-label">Technical Title</label>
//         <input type="text" className={`form-control ${consultantTitleError ? 'is-invalid' : ''}`} placeholder="Enter consultant title" value={consultantTitle} onChange={(e) => setConsultantTitle(e.target.value)} />
//         {consultantTitleError && <div className="invalid-feedback">{consultantTitleError}</div>}
//       </div>
//       <div className="row mb-3">
//         <div className="col-md-6">
//           <label className="form-label">Vacancies</label>
//           <input type="text" className={`form-control ${consultantVacanciesError ? 'is-invalid' : ''}`} placeholder="Number of vacancies" value={consultantVacancies} onChange={(e) => setConsultantVacancies(e.target.value)} />
//           {consultantVacanciesError && <div className="invalid-feedback">{consultantVacanciesError}</div>}
//         </div>
//         <div className="col-md-6">
//           <label className="form-label">Experience</label>
//           <input type="text" className={`form-control ${consultantExperienceError ? 'is-invalid' : ''}`} placeholder="e.g., 5+ years" value={consultantExperience} onChange={(e) => setConsultantExperience(e.target.value)} />
//           {consultantExperienceError && <div className="invalid-feedback">{consultantExperienceError}</div>}
//         </div>
//       </div>
//       <div className="row mb-3">
//         <div className="col-md-6">
//           <label className="form-label">Locations</label>
//           <input type="text" className={`form-control ${consultantLocationsError ? 'is-invalid' : ''}`} placeholder="e.g., Remote, On-site" value={consultantLocations} onChange={(e) => setConsultantLocations(e.target.value)} />
//           {consultantLocationsError && <div className="invalid-feedback">{consultantLocationsError}</div>}
//         </div>
//         <div className="col-md-6">
//           <label className="form-label">Type</label>
//           <select className={`form-select ${consultantTypeError ? 'is-invalid' : ''}`} value={consultantType} onChange={(e) => setConsultantType(e.target.value)}>
//             <option value="" disabled>Choose consultant type...</option>
//             <option value="Contract">Contract</option>
//             <option value="Project-Based">Project-Based</option>
//             <option value="Full-Time">Full-Time</option>
//           </select>
//           {consultantTypeError && <div className="invalid-feedback">{consultantTypeError}</div>}
//         </div>
//       </div>
//     </>
//   );

//   return (
//     <div>
//       <style>
//         {`
//           .ql-font-arial { font-family: "Arial", sans-serif; }
//           .ql-font-georgia { font-family: "Georgia", serif; }
//           .ql-font-comic-sans-ms { font-family: "Comic Sans MS", cursive, sans-serif; }
//           .ql-font-courier-new { font-family: "Courier New", monospace; }
//           .ql-font-helvetica { font-family: "Helvetica", sans-serif; }
//           .ql-font-lucida { font-family: "Lucida Sans Unicode", sans-serif; }

//           .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="arial"]::before,
//           .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="arial"]::before { content: 'Arial'; font-family: "Arial", sans-serif; }
//           .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="georgia"]::before,
//           .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="georgia"]::before { content: 'Georgia'; font-family: "Georgia", serif; }
//           .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="comic-sans-ms"]::before,
//           .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="comic-sans-ms"]::before { content: 'Comic Sans'; font-family: "Comic Sans MS", cursive, sans-serif; }
//           .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="courier-new"]::before,
//           .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="courier-new"]::before { content: 'Courier New'; font-family: "Courier New", monospace; }
//           .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="helvetica"]::before,
//           .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="helvetica"]::before { content: 'Helvetica'; font-family: "Helvetica", sans-serif; }
//           .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="lucida"]::before,
//           .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="lucida"]::before { content: 'Lucida'; font-family: "Lucida Sans Unicode", sans-serif; }

//           .ql-editor h1, .ql-editor h2, .ql-editor h3, .ql-editor h4, .ql-editor h5, .ql-editor h6 { font-family: inherit !important; }
//         `}
//       </style>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <main className="main">
//           <div className="job-posting-page-wrapper">
//             <div className="job-form-container">
//               <div className="job-posting-form-inner">
//                 <h2 className="gradient-text mb-4 text-center">
//                   Posting Form
//                 </h2>
//                 <form onSubmit={handleSubmit} noValidate>
//                   <div className="mb-3">
//                     <label className="form-label">Post As</label>
//                     <select
//                       className="form-select"
//                       value={postType}
//                       onChange={handlePostTypeChange}
//                     >
//                       <option value="job">Job Opening</option>
//                       <option value="business">Business Opportunity</option>
//                       <option value="consultant">Technical Consultant</option>
//                     </select>
//                   </div>
                  
//                   <hr/>

//                   {postType === 'job' && renderJobOpeningForm()}
//                   {postType === 'business' && renderBusinessOpportunityForm()}
//                   {postType === 'consultant' && renderTechnicalConsultantForm()}

//                   <div className="mb-3">
//                     <label className="form-label">Description</label>
//                     <ReactQuill theme="snow" value={description} onChange={setDescription} modules={quillModules} formats={quillFormats} placeholder="Enter description, responsibilities..." />
//                     {descriptionError && <div className="text-danger small mt-1">{descriptionError}</div>}
//                   </div>

//                   <div className="pt-3 d-grid gap-2">
//                     <button type="submit" className="btn btn-primary btn-lg" disabled={isLoading}>
//                       {isLoading ? (<><span className="spinner-border spinner-border-sm me-2"></span>Posting...</>) : "Post"}
//                     </button>
//                     {isEditing && (
//                       <button type="button" className="btn btn-secondary btn-lg" onClick={() => setIsEditing(false)} disabled={isLoading}>
//                         Cancel Edit
//                       </button>
//                     )}
//                   </div>
//                 </form>
//               </div>
//             </div>
//             <div className="job-list-container">
//               <h5 className="mb-3 text-secondary">Recently Posted Jobs</h5>
//               {isLoadingPostedJobs ? <div className="text-center mt-3"><div className="spinner-border text-primary"></div></div> :
//                postedJobsError ? <div className="alert alert-danger">{postedJobsError}</div> :
//                postedJobs.length === 0 ? <p className="text-muted text-center">No jobs posted yet.</p> :
//                [...postedJobs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((job) => (
//                 <div key={job.job_id} className="job-list-card">
//                   <div className="d-flex justify-content-between">
//                     <h6 className="card-title" title={job.jobtitle}>{job.jobtitle || 'No Title'}</h6>
//                     <span className={`job-status ${job.status === 'Active' ? 'active' : 'inactive'}`}>{job.status}</span>
//                   </div>
//                   <p className="job-id mb-2">ID: {job.job_id}</p>
//                   <div className="action-buttons">
//                     <button className="btn btn-edit" onClick={() => handleEditJob(job.job_id)} disabled={isLoading || (isEditing && editingJobId !== job.job_id)}>
//                         <i className="fas fa-edit"></i>
//                     </button>
//                     <button className={`btn btn-toggle ${job.status === 'Active' ? 'btn-deactivate' : 'btn-activate'}`} onClick={() => handleToggleJobStatus(job)} disabled={updatingStatusJobId === job.job_id || isLoading || isEditing}>
//                       {updatingStatusJobId === job.job_id ? <span className="spinner-border spinner-border-sm"></span> : job.status === 'Active' ? 'Deactivate' : 'Activate'}
//                     </button>
//                   </div>
//                   <p className="posted-date">Posted: {job.created_at ? new Date(job.created_at).toLocaleDateString() : 'N/A'}</p>
//                 </div>
//               ))}
//             </div>
//           </div>
//         </main>
//       </div>
//     </div>
//   );
// };

// export default Jobposting;





// import React, { useState, useEffect, useCallback } from "react";
// import axios from "axios";
// import Swal from "sweetalert2";
// import Sidebar from "./Sidebar";
// import DashNavbar from "./DashNavbar";
// import ReactQuill, { Quill } from 'react-quill'; // Import Quill
// import 'react-quill/dist/quill.snow.css';
// import '../css/JobPosting.css';

// // --- Quill Font & Style Configuration ---

// // Whitelist the fonts that will appear in the dropdown
// const Font = Quill.import('formats/font');
// Font.whitelist = ['arial', 'georgia', 'comic-sans-ms', 'courier-new', 'helvetica', 'lucida'];
// Quill.register(Font, true);

// const stripHtml = (html) => {
//   if (!html || html === '<p><br></p>' || html === '<p></p>') return "";
//   const doc = new DOMParser().parseFromString(html, 'text/html');
//   return doc.body.textContent || "";
// };

// const quillModules = {
//   toolbar: [
//     [{ 'header': [1, 2, 3, 4, 5, 6, false] }, { 'font': Font.whitelist }],
//     ['bold', 'italic', 'underline', 'strike'],
//     [{ 'list': 'ordered'}, { 'list': 'bullet' }], [{ 'indent': '-1'}, { 'indent': '+1' }],
//     ['link'], ['clean']
//   ],
// };

// const quillFormats = [
//   'header', 'font', 'bold', 'italic', 'underline', 'strike', 
//   'list', 'bullet', 'indent', 'link'
// ];

// const editApiBaseUrl = "https://raasbackend.vetrinahealthcare.com/";

// const Jobposting = () => {
//   // Form State
//   const [jobId, setJobId] = useState("JOB-1");
//   const [jobtitle, setJobTitle] = useState("");
//   const [description, setDescription] = useState("");
//   const [vacancies, setVacancies] = useState("");
//   const [experience, setExperience] = useState("");
//   const [jobLocationsString, setJobLocationsString] = useState("");
//   const [roleType, setRoleType] = useState("");
//   const [openingPostAs, setOpeningPostAs] = useState("");

//   // Error State
//   const [jobTitleError, setJobTitleError] = useState("");
//   const [vacanciesError, setVacanciesError] = useState("");
//   const [experienceError, setExperienceError] = useState("");
//   const [jobLocationError, setJobLocationError] = useState("");
//   const [roleTypeError, setRoleTypeError] = useState("");
//   const [descriptionError, setDescriptionError] = useState("");
//   const [openingPostAsError, setOpeningPostAsError] = useState("");

//   // Control State
//   const [isLoading, setIsLoading] = useState(false);
//   const [isEditing, setIsEditing] = useState(false);
//   const [editingJobId, setEditingJobId] = useState(null);

//   // Posted Jobs List State
//   const [postedJobs, setPostedJobs] = useState([]);
//   const [isLoadingPostedJobs, setIsLoadingPostedJobs] = useState(true);
//   const [postedJobsError, setPostedJobsError] = useState(null);
//   const [updatingStatusJobId, setUpdatingStatusJobId] = useState(null);

//   // Dropdown Options
//   const roleOptions = [
//     { value: "Full Time", label: "Full Time" }, { value: "Part Time", label: "Part Time" },
//     { value: "Contractual", label: "Contractual" }, { value: "Intern", label: "Intern" },
//   ];
//   const postingTypeOptions = [
//     { value: "Job_Opening", label: "Job Opening" },
//     { value: "Business_Opportunity", label: "Business Opportunity" },
//     { value: "Consultancy_Opportunity", label: "Consultancy Opportunity" },
//   ];

//   const generateNextJobId = useCallback((lastJobId) => {
//     const match = lastJobId?.match(/\d+$/);
//     if (match) return `JOB-${parseInt(match[0], 10) + 1}`;
//     return "JOB-1";
//   }, []);

//   const fetchPostedJobs = useCallback(async () => {
//     setIsLoadingPostedJobs(true);
//     setPostedJobsError(null);
//     try {
//       const response = await axios.get(`${editApiBaseUrl}/jobpostings_list/`);
//       setPostedJobs(response.data || []);
//     } catch (error) {
//       setPostedJobsError("Failed to load jobs.");
//     } finally {
//       setIsLoadingPostedJobs(false);
//     }
//   }, []);

//   const resetFormToCreateMode = useCallback(async () => {
//     setIsEditing(false); setEditingJobId(null);
//     setJobTitle(""); setDescription(""); setVacancies(""); setExperience("");
//     setJobLocationsString(""); setRoleType(""); setOpeningPostAs("");

//     // Clear all errors
//     setJobTitleError(""); setVacanciesError(""); setExperienceError("");
//     setJobLocationError(""); setRoleTypeError(""); setDescriptionError("");
//     setOpeningPostAsError("");

//     try {
//       const response = await axios.get(`${editApiBaseUrl}/last-job-id/`);
//       setJobId(generateNextJobId(response.data.job_id || "JOB-0"));
//     } catch {
//       setJobId(generateNextJobId(localStorage.getItem("lastJobId") || "JOB-0"));
//     }
//   }, [generateNextJobId]);

//   useEffect(() => {
//     if (!isEditing) {
//       resetFormToCreateMode();
//     }
//   }, [isEditing, resetFormToCreateMode]);

//   useEffect(() => {
//     fetchPostedJobs();
//   }, [fetchPostedJobs]);
  
//   const validateAllFields = () => {
//     const titleError = !jobtitle.trim() ? "Job Title is required." : "";
//     const vacanciesError = !vacancies || !/^\d+$/.test(vacancies) || parseInt(vacancies, 10) <= 0 ? "Vacancies must be a positive number." : "";
//     const experienceError = !experience.trim() ? "Experience is required." : "";
//     const locationError = !jobLocationsString.trim() ? "Job Location is required." : "";
//     const roleError = !roleType ? "Please select a role type." : "";
//     const descError = !stripHtml(description).trim() ? "Description is required." : "";
//     const openingPostAsErr = !openingPostAs ? "Please select a posting type." : "";
    
//     setJobTitleError(titleError);
//     setVacanciesError(vacanciesError);
//     setExperienceError(experienceError);
//     setJobLocationError(locationError);
//     setRoleTypeError(roleError);
//     setDescriptionError(descError);
//     setOpeningPostAsError(openingPostAsErr);
    
//     return !(titleError || vacanciesError || experienceError || locationError || roleError || descError || openingPostAsErr);
//   };

//   const handleDescriptionChange = (content) => {
//     setDescription(content);
//     if (descriptionError) {
//       validateAllFields();
//     }
//   };

//   const handleEditJob = async (jobIdToEdit) => {
//     document.querySelector('.job-form-container')?.scrollTo({ top: 0, behavior: 'smooth' });
//     setIsLoading(true);
//     try {
//       const response = await axios.get(`${editApiBaseUrl}/get_job_posting/${jobIdToEdit}/`);
//       const jobData = response.data;
//       if (jobData) {
//         setIsEditing(true); setEditingJobId(jobData.job_id); setJobId(jobData.job_id);
//         setJobTitle(jobData.jobtitle || ""); setDescription(jobData.description || "");
//         setVacancies(jobData.vacancies?.toString() || ""); setExperience(jobData.experience || "");
//         setJobLocationsString(jobData.job_location || ""); setRoleType(jobData.role_type || "");
        
//         // *** ROBUST FIX: Explicitly handle all known category values to set dropdown state ***
//         const rawCategory = jobData.opening_post_as;
//         if (rawCategory === "Job Opening" || rawCategory === "Job_Opening") {
//           setOpeningPostAs("Job_Opening");
//         } else if (rawCategory === "Business_Opportunity") {
//           setOpeningPostAs("Business_Opportunity");
//         } else if (rawCategory === "Consultancy_Opportunity") {
//           setOpeningPostAs("Consultancy_Opportunity");
//         } else {
//           setOpeningPostAs(""); // Default to empty if unknown value is received
//         }

//         // Clear errors
//         setJobTitleError(""); setVacanciesError(""); setExperienceError("");
//         setJobLocationError(""); setRoleTypeError(""); setDescriptionError("");
//         setOpeningPostAsError("");
//       } else {
//         Swal.fire("Error", "Could not load job details.", "error");
//       }
//     } catch (error) {
//       Swal.fire("Load Error", "Failed to load job details for editing.", "error");
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!validateAllFields()) {
//       Swal.fire({ title: "Validation Error", text: "Please fix the errors before submitting.", icon: "warning", timer: 3000 });
//       return;
//     }
//     setIsLoading(true);

//     const jobData = {
//       jobtitle, description, vacancies: parseInt(vacancies), experience,
//       job_location: jobLocationsString, role_type: roleType,
//       opening_post_as: openingPostAs, 
//     };

//     const url = isEditing ? `${editApiBaseUrl}/update_job_posting/${editingJobId}/` : `${editApiBaseUrl}/JobPosting_view/`;
//     const method = isEditing ? 'put' : 'post';
    
//     if (!isEditing) {
//         try {
//             const res = await axios.get(`${editApiBaseUrl}/last-job-id/`);
//             jobData.job_id = generateNextJobId(res.data.job_id || "JOB-0");
//         } catch {
//             jobData.job_id = generateNextJobId(localStorage.getItem("lastJobId") || "JOB-0");
//         }
//     }

//     try {
//         await axios[method](url, jobData);
//         if (!isEditing) localStorage.setItem("lastJobId", jobData.job_id);
//         await Swal.fire({ title: isEditing ? "Job Updated!" : "Job Posted!", icon: "success", timer: 1500, showConfirmButton: false });
//         fetchPostedJobs();
//         await resetFormToCreateMode();
//     } catch (error) {
//         Swal.fire("Error", `An error occurred while ${isEditing ? 'updating' : 'posting'} the job.`, "error");
//     } finally {
//         setIsLoading(false);
//     }
//   };

//   const handleToggleJobStatus = async (job) => {
//     const targetStatus = job.status === "Active" ? "Inactive" : "Active";
//     const action = targetStatus === "Active" ? "activate" : "deactivate";
//     setUpdatingStatusJobId(job.job_id);
//     try {
//       await axios.put(`${editApiBaseUrl}/jobpostings/${job.job_id}/${action}/`);
//       Swal.fire({ title: "Status Updated!", text: `Job ${job.job_id} is now ${targetStatus}.`, icon: "success", timer: 1500, showConfirmButton: false });
//       fetchPostedJobs();
//     } catch (error) {
//       Swal.fire("Update Error", `Failed to ${action} job.`, "error");
//     } finally {
//       setUpdatingStatusJobId(null);
//     }
//   };

//   return (
//     <div>
//       {/* *** FIX: Add comprehensive CSS for Quill fonts and headings *** */}
//       <style>
//         {`
//           /* Define the font families for the editor content */
//           .ql-font-arial { font-family: "Arial", sans-serif; }
//           .ql-font-georgia { font-family: "Georgia", serif; }
//           .ql-font-comic-sans-ms { font-family: "Comic Sans MS", cursive, sans-serif; }
//           .ql-font-courier-new { font-family: "Courier New", monospace; }
//           .ql-font-helvetica { font-family: "Helvetica", sans-serif; }
//           .ql-font-lucida { font-family: "Lucida Sans Unicode", sans-serif; }

//           /* Style the dropdown labels themselves */
//           .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="arial"]::before,
//           .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="arial"]::before {
//             content: 'Arial';
//             font-family: "Arial", sans-serif;
//           }
//           .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="georgia"]::before,
//           .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="georgia"]::before {
//             content: 'Georgia';
//             font-family: "Georgia", serif;
//           }
//           .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="comic-sans-ms"]::before,
//           .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="comic-sans-ms"]::before {
//             content: 'Comic Sans';
//             font-family: "Comic Sans MS", cursive, sans-serif;
//           }
//           .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="courier-new"]::before,
//           .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="courier-new"]::before {
//             content: 'Courier New';
//             font-family: "Courier New", monospace;
//           }
//           .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="helvetica"]::before,
//           .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="helvetica"]::before {
//             content: 'Helvetica';
//             font-family: "Helvetica", sans-serif;
//           }
//           .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="lucida"]::before,
//           .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="lucida"]::before {
//             content: 'Lucida';
//             font-family: "Lucida Sans Unicode", sans-serif;
//           }

//           /* Ensure headings inherit the currently selected font family */
//           .ql-editor h1, .ql-editor h2, .ql-editor h3, .ql-editor h4, .ql-editor h5, .ql-editor h6 {
//             font-family: inherit !important;
//           }
//         `}
//       </style>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <main className="main">
//           <div className="job-posting-page-wrapper">
//             <div className="job-form-container">
//               <div className="job-posting-form-inner">
//                 <h2 className="gradient-text mb-4 text-center">
//                   {isEditing ? `Edit Job Posting` : "Job Posting Form"}
//                 </h2>
//                 <form onSubmit={handleSubmit} noValidate>
//                   {/* Form fields remain the same */}
//                   <div className="mb-3">
//                     <label className="form-label">Job ID</label>
//                     <input type="text" className="form-control" value={jobId} readOnly style={{ backgroundColor: '#e9ecef' }} />
//                   </div>
//                   <div className="mb-3">
//                     <label className="form-label">Job Title</label>
//                     <input type="text" className={`form-control ${jobTitleError ? 'is-invalid' : ''}`} placeholder="Enter role (e.g., Software Engineer)" value={jobtitle} onChange={(e) => setJobTitle(e.target.value)} />
//                     {jobTitleError && <div className="invalid-feedback">{jobTitleError}</div>}
//                   </div>
//                   <div className="row mb-3">
//                     <div className="col-md-6">
//                       <label className="form-label">Vacancies</label>
//                       <input type="text" className={`form-control ${vacanciesError ? 'is-invalid' : ''}`} placeholder="Number of vacancies" value={vacancies} onChange={(e) => setVacancies(e.target.value)} />
//                       {vacanciesError && <div className="invalid-feedback">{vacanciesError}</div>}
//                     </div>
//                     <div className="col-md-6">
//                       <label className="form-label">Experience</label>
//                       <input type="text" className={`form-control ${experienceError ? 'is-invalid' : ''}`} placeholder="e.g., 0-1 years, 2+ years" value={experience} onChange={(e) => setExperience(e.target.value)} />
//                       {experienceError && <div className="invalid-feedback">{experienceError}</div>}
//                     </div>
//                   </div>
//                   <div className="row mb-3">
//                     <div className="col-md-6">
//                       <label className="form-label">Job Location(s)</label>
//                       <input type="text" className={`form-control ${jobLocationError ? 'is-invalid' : ''}`} placeholder="Locations, comma-separated" value={jobLocationsString} onChange={(e) => setJobLocationsString(e.target.value)} />
//                       {jobLocationError && <div className="invalid-feedback">{jobLocationError}</div>}
//                     </div>
//                     <div className="col-md-6">
//                       <label className="form-label">Role Type</label>
//                       <select className={`form-select ${roleTypeError ? 'is-invalid' : ''}`} value={roleType} onChange={(e) => setRoleType(e.target.value)}>
//                         <option value="" disabled>Choose role type...</option>
//                         {roleOptions.map((option) => (<option key={option.value} value={option.value}>{option.label}</option>))}
//                       </select>
//                       {roleTypeError && <div className="invalid-feedback">{roleTypeError}</div>}
//                     </div>
//                   </div>
//                    <div className="mb-3">
//                     <label className="form-label">Post As</label>
//                     <select
//                       className={`form-select ${openingPostAsError ? 'is-invalid' : ''}`}
//                       value={openingPostAs}
//                       onChange={(e) => setOpeningPostAs(e.target.value)}
//                     >
//                       <option value="" disabled>Choose posting type...</option>
//                       {postingTypeOptions.map((option) => (
//                         <option key={option.value} value={option.value}>
//                           {option.label}
//                         </option>
//                       ))}
//                     </select>
//                     {openingPostAsError && <div className="invalid-feedback">{openingPostAsError}</div>}
//                   </div>
//                   <div className="mb-3">
//                     <label className="form-label">Description</label>
//                     <ReactQuill theme="snow" value={description} onChange={handleDescriptionChange} modules={quillModules} formats={quillFormats} placeholder="Enter job description, responsibilities..." />
//                     {descriptionError && <div className="text-danger small mt-1">{descriptionError}</div>}
//                   </div>
//                   <div className="pt-3 d-grid gap-2">
//                     <button type="submit" className="btn btn-primary btn-lg" disabled={isLoading}>
//                       {isLoading ? (<><span className="spinner-border spinner-border-sm me-2"></span>{isEditing ? "Updating..." : "Posting..."}</>) : (isEditing ? "Update Job" : "Post Job")}
//                     </button>
//                     {isEditing && (
//                       <button type="button" className="btn btn-secondary btn-lg" onClick={resetFormToCreateMode} disabled={isLoading}>
//                         Cancel Edit
//                       </button>
//                     )}
//                   </div>
//                 </form>
//               </div>
//             </div>
//             <div className="job-list-container">
//               <h5 className="mb-3 text-secondary">Recently Posted Jobs</h5>
//               {isLoadingPostedJobs ? <div className="text-center mt-3"><div className="spinner-border text-primary"></div></div> :
//                postedJobsError ? <div className="alert alert-danger">{postedJobsError}</div> :
//                postedJobs.length === 0 ? <p className="text-muted text-center">No jobs posted yet.</p> :
//                [...postedJobs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((job) => (
//                 <div key={job.job_id} className="job-list-card">
//                   <div className="d-flex justify-content-between">
//                     <h6 className="card-title" title={job.jobtitle}>{job.jobtitle || 'No Title'}</h6>
//                     <span className={`job-status ${job.status === 'Active' ? 'active' : 'inactive'}`}>{job.status}</span>
//                   </div>
//                   <p className="job-id mb-2">ID: {job.job_id}</p>
//                   <div className="action-buttons">
//                     <button className="btn btn-edit" onClick={() => handleEditJob(job.job_id)} disabled={isLoading || (isEditing && editingJobId !== job.job_id)}>
//                         <i className="fas fa-edit"></i>
//                     </button>
//                     <button className={`btn btn-toggle ${job.status === 'Active' ? 'btn-deactivate' : 'btn-activate'}`} onClick={() => handleToggleJobStatus(job)} disabled={updatingStatusJobId === job.job_id || isLoading || isEditing}>
//                       {updatingStatusJobId === job.job_id ? <span className="spinner-border spinner-border-sm"></span> : job.status === 'Active' ? 'Deactivate' : 'Activate'}
//                     </button>
//                   </div>
//                   <p className="posted-date">Posted: {job.created_at ? new Date(job.created_at).toLocaleDateString() : 'N/A'}</p>
//                 </div>
//               ))}
//             </div>
//           </div>
//         </main>
//       </div>
//     </div>
//   );
// };

// export default Jobposting;

// import React, { useState, useEffect, useCallback } from "react";
// import axios from "axios";
// import Swal from "sweetalert2";
// import Sidebar from "./Sidebar";
// import DashNavbar from "./DashNavbar";
// import ReactQuill from 'react-quill';
// import 'react-quill/dist/quill.snow.css';
// import '../css/JobPosting.css';

// const stripHtml = (html) => {
//   if (!html || html === '<p><br></p>' || html === '<p></p>') return "";
//   const doc = new DOMParser().parseFromString(html, 'text/html');
//   return doc.body.textContent || "";
// };

// const quillModules = {
//   toolbar: [
//     [{ 'header': [1, 2, 3, 4, 5, 6, false] }], ['bold', 'italic', 'underline', 'strike'],
//     [{ 'list': 'ordered'}, { 'list': 'bullet' }], [{ 'indent': '-1'}, { 'indent': '+1' }],
//     ['link'], ['clean']
//   ],
// };

// const quillFormats = ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'indent', 'link'];

// const editApiBaseUrl = "https://raasbackend.vetrinahealthcare.com/";

// const Jobposting = () => {
//   // Form State
//   const [jobId, setJobId] = useState("JOB-1");
//   const [jobtitle, setJobTitle] = useState("");
//   const [description, setDescription] = useState("");
//   const [vacancies, setVacancies] = useState("");
//   const [experience, setExperience] = useState("");
//   const [jobLocationsString, setJobLocationsString] = useState("");
//   const [roleType, setRoleType] = useState("");
//   const [openingPostAs, setOpeningPostAs] = useState("");

//   // Error State
//   const [jobTitleError, setJobTitleError] = useState("");
//   const [vacanciesError, setVacanciesError] = useState("");
//   const [experienceError, setExperienceError] = useState("");
//   const [jobLocationError, setJobLocationError] = useState("");
//   const [roleTypeError, setRoleTypeError] = useState("");
//   const [descriptionError, setDescriptionError] = useState("");
//   const [openingPostAsError, setOpeningPostAsError] = useState("");

//   // Control State
//   const [isLoading, setIsLoading] = useState(false);
//   const [isEditing, setIsEditing] = useState(false);
//   const [editingJobId, setEditingJobId] = useState(null);

//   // Posted Jobs List State
//   const [postedJobs, setPostedJobs] = useState([]);
//   const [isLoadingPostedJobs, setIsLoadingPostedJobs] = useState(true);
//   const [postedJobsError, setPostedJobsError] = useState(null);
//   const [updatingStatusJobId, setUpdatingStatusJobId] = useState(null);

//   // Dropdown Options
//   const roleOptions = [
//     { value: "Full Time", label: "Full Time" }, { value: "Part Time", label: "Part Time" },
//     { value: "Contractual", label: "Contractual" }, { value: "Intern", label: "Intern" },
//   ];
//   const postingTypeOptions = [
//     { value: "Job_Opening", label: "Job Opening" },
//     { value: "Business_Opportunity", label: "Business Opportunity" },
//     { value: "Consultancy_Opportunity", label: "Consultancy Opportunity" },
//   ];

//   const generateNextJobId = useCallback((lastJobId) => {
//     const match = lastJobId?.match(/\d+$/);
//     if (match) return `JOB-${parseInt(match[0], 10) + 1}`;
//     return "JOB-1";
//   }, []);

//   const fetchPostedJobs = useCallback(async () => {
//     setIsLoadingPostedJobs(true);
//     setPostedJobsError(null);
//     try {
//       const response = await axios.get(`${editApiBaseUrl}/jobpostings_list/`);
//       setPostedJobs(response.data || []);
//     } catch (error) {
//       setPostedJobsError("Failed to load jobs.");
//     } finally {
//       setIsLoadingPostedJobs(false);
//     }
//   }, []);

//   const resetFormToCreateMode = useCallback(async () => {
//     setIsEditing(false); setEditingJobId(null);
//     setJobTitle(""); setDescription(""); setVacancies(""); setExperience("");
//     setJobLocationsString(""); setRoleType(""); setOpeningPostAs("");

//     // Clear all errors
//     setJobTitleError(""); setVacanciesError(""); setExperienceError("");
//     setJobLocationError(""); setRoleTypeError(""); setDescriptionError("");
//     setOpeningPostAsError("");

//     try {
//       const response = await axios.get(`${editApiBaseUrl}/last-job-id/`);
//       setJobId(generateNextJobId(response.data.job_id || "JOB-0"));
//     } catch {
//       setJobId(generateNextJobId(localStorage.getItem("lastJobId") || "JOB-0"));
//     }
//   }, [generateNextJobId]);

//   useEffect(() => {
//     if (!isEditing) {
//       resetFormToCreateMode();
//     }
//   }, [isEditing, resetFormToCreateMode]);

//   useEffect(() => {
//     fetchPostedJobs();
//   }, [fetchPostedJobs]);
  
//   const validateAllFields = () => {
//     const titleError = !jobtitle.trim() ? "Job Title is required." : "";
//     const vacanciesError = !vacancies || !/^\d+$/.test(vacancies) || parseInt(vacancies, 10) <= 0 ? "Vacancies must be a positive number." : "";
//     const experienceError = !experience.trim() ? "Experience is required." : "";
//     const locationError = !jobLocationsString.trim() ? "Job Location is required." : "";
//     const roleError = !roleType ? "Please select a role type." : "";
//     const descError = !stripHtml(description).trim() ? "Description is required." : "";
//     const openingPostAsErr = !openingPostAs ? "Please select a posting type." : "";
    
//     setJobTitleError(titleError);
//     setVacanciesError(vacanciesError);
//     setExperienceError(experienceError);
//     setJobLocationError(locationError);
//     setRoleTypeError(roleError);
//     setDescriptionError(descError);
//     setOpeningPostAsError(openingPostAsErr);
    
//     return !(titleError || vacanciesError || experienceError || locationError || roleError || descError || openingPostAsErr);
//   };

//   const handleDescriptionChange = (content) => {
//     setDescription(content);
//     if (descriptionError) {
//       validateAllFields();
//     }
//   };

//   const handleEditJob = async (jobIdToEdit) => {
//     document.querySelector('.job-form-container')?.scrollTo({ top: 0, behavior: 'smooth' });
//     setIsLoading(true);
//     try {
//       const response = await axios.get(`${editApiBaseUrl}/get_job_posting/${jobIdToEdit}/`);
//       const jobData = response.data;
//       if (jobData) {
//         setIsEditing(true); setEditingJobId(jobData.job_id); setJobId(jobData.job_id);
//         setJobTitle(jobData.jobtitle || ""); setDescription(jobData.description || "");
//         setVacancies(jobData.vacancies?.toString() || ""); setExperience(jobData.experience || "");
//         setJobLocationsString(jobData.job_location || ""); setRoleType(jobData.role_type || "");
//         setOpeningPostAs(jobData.opening_post_as || "");

//         // Clear errors
//         setJobTitleError(""); setVacanciesError(""); setExperienceError("");
//         setJobLocationError(""); setRoleTypeError(""); setDescriptionError("");
//         setOpeningPostAsError("");
//       } else {
//         Swal.fire("Error", "Could not load job details.", "error");
//       }
//     } catch (error) {
//       Swal.fire("Load Error", "Failed to load job details for editing.", "error");
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!validateAllFields()) {
//       Swal.fire({ title: "Validation Error", text: "Please fix the errors before submitting.", icon: "warning", timer: 3000 });
//       return;
//     }
//     setIsLoading(true);

//     // This payload works for both creating and updating (the API ignores job_id on update)
//     const jobData = {
//       jobtitle, description, vacancies: parseInt(vacancies), experience,
//       job_location: jobLocationsString, role_type: roleType,
//       opening_post_as: openingPostAs, 
//     };

//     const url = isEditing ? `${editApiBaseUrl}/update_job_posting/${editingJobId}/` : `${editApiBaseUrl}/JobPosting_view/`;
//     const method = isEditing ? 'put' : 'post';
    
//     // Only generate a new job_id when creating
//     if (!isEditing) {
//         try {
//             const res = await axios.get(`${editApiBaseUrl}/last-job-id/`);
//             jobData.job_id = generateNextJobId(res.data.job_id || "JOB-0");
//         } catch {
//             jobData.job_id = generateNextJobId(localStorage.getItem("lastJobId") || "JOB-0");
//         }
//     }

//     try {
//         await axios[method](url, jobData);
//         if (!isEditing) localStorage.setItem("lastJobId", jobData.job_id);
//         await Swal.fire({ title: isEditing ? "Job Updated!" : "Job Posted!", icon: "success", timer: 1500, showConfirmButton: false });
//         fetchPostedJobs();
//         await resetFormToCreateMode();
//     } catch (error) {
//         Swal.fire("Error", `An error occurred while ${isEditing ? 'updating' : 'posting'} the job.`, "error");
//     } finally {
//         setIsLoading(false);
//     }
//   };

//   const handleToggleJobStatus = async (job) => {
//     const targetStatus = job.status === "Active" ? "Inactive" : "Active";
//     const action = targetStatus === "Active" ? "activate" : "deactivate";
//     setUpdatingStatusJobId(job.job_id);
//     try {
//       await axios.put(`${editApiBaseUrl}/jobpostings/${job.job_id}/${action}/`);
//       Swal.fire({ title: "Status Updated!", text: `Job ${job.job_id} is now ${targetStatus}.`, icon: "success", timer: 1500, showConfirmButton: false });
//       fetchPostedJobs();
//     } catch (error) {
//       Swal.fire("Update Error", `Failed to ${action} job.`, "error");
//     } finally {
//       setUpdatingStatusJobId(null);
//     }
//   };

//   return (
//     <div>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <main className="main">
//           <div className="job-posting-page-wrapper">
//             <div className="job-form-container">
//               <div className="job-posting-form-inner">
//                 <h2 className="gradient-text mb-4 text-center">
//                   {isEditing ? `Edit Job Posting` : "Job Posting Form"}
//                 </h2>
//                 <form onSubmit={handleSubmit} noValidate>
//                   <div className="mb-3">
//                     <label className="form-label">Job ID</label>
//                     <input type="text" className="form-control" value={jobId} readOnly style={{ backgroundColor: '#e9ecef' }} />
//                   </div>
//                   <div className="mb-3">
//                     <label className="form-label">Job Title</label>
//                     <input type="text" className={`form-control ${jobTitleError ? 'is-invalid' : ''}`} placeholder="Enter role (e.g., Software Engineer)" value={jobtitle} onChange={(e) => setJobTitle(e.target.value)} />
//                     {jobTitleError && <div className="invalid-feedback">{jobTitleError}</div>}
//                   </div>
//                   <div className="row mb-3">
//                     <div className="col-md-6">
//                       <label className="form-label">Vacancies</label>
//                       <input type="text" className={`form-control ${vacanciesError ? 'is-invalid' : ''}`} placeholder="Number of vacancies" value={vacancies} onChange={(e) => setVacancies(e.target.value)} />
//                       {vacanciesError && <div className="invalid-feedback">{vacanciesError}</div>}
//                     </div>
//                     <div className="col-md-6">
//                       <label className="form-label">Experience</label>
//                       <input type="text" className={`form-control ${experienceError ? 'is-invalid' : ''}`} placeholder="e.g., 0-1 years, 2+ years" value={experience} onChange={(e) => setExperience(e.target.value)} />
//                       {experienceError && <div className="invalid-feedback">{experienceError}</div>}
//                     </div>
//                   </div>
//                   <div className="row mb-3">
//                     <div className="col-md-6">
//                       <label className="form-label">Job Location(s)</label>
//                       <input type="text" className={`form-control ${jobLocationError ? 'is-invalid' : ''}`} placeholder="Locations, comma-separated" value={jobLocationsString} onChange={(e) => setJobLocationsString(e.target.value)} />
//                       {jobLocationError && <div className="invalid-feedback">{jobLocationError}</div>}
//                     </div>
//                     <div className="col-md-6">
//                       <label className="form-label">Role Type</label>
//                       <select className={`form-select ${roleTypeError ? 'is-invalid' : ''}`} value={roleType} onChange={(e) => setRoleType(e.target.value)}>
//                         <option value="" disabled>Choose role type...</option>
//                         {roleOptions.map((option) => (<option key={option.value} value={option.value}>{option.label}</option>))}
//                       </select>
//                       {roleTypeError && <div className="invalid-feedback">{roleTypeError}</div>}
//                     </div>
//                   </div>
//                    <div className="mb-3">
//                     <label className="form-label">Post As</label>
//                     <select
//                       className={`form-select ${openingPostAsError ? 'is-invalid' : ''}`}
//                       value={openingPostAs}
//                       onChange={(e) => setOpeningPostAs(e.target.value)}
//                       // The opening_post_as field is editable in both create and update modes as per the new payload
//                     >
//                       <option value="" disabled>Choose posting type...</option>
//                       {postingTypeOptions.map((option) => (
//                         <option key={option.value} value={option.value}>
//                           {option.label}
//                         </option>
//                       ))}
//                     </select>
//                     {openingPostAsError && <div className="invalid-feedback">{openingPostAsError}</div>}
//                   </div>
//                   <div className="mb-3">
//                     <label className="form-label">Description</label>
//                     <ReactQuill theme="snow" value={description} onChange={handleDescriptionChange} modules={quillModules} formats={quillFormats} placeholder="Enter job description, responsibilities..." />
//                     {descriptionError && <div className="text-danger small mt-1">{descriptionError}</div>}
//                   </div>
//                   <div className="pt-3 d-grid gap-2">
//                     <button type="submit" className="btn btn-primary btn-lg" disabled={isLoading}>
//                       {isLoading ? (<><span className="spinner-border spinner-border-sm me-2"></span>{isEditing ? "Updating..." : "Posting..."}</>) : (isEditing ? "Update Job" : "Post Job")}
//                     </button>
//                     {isEditing && (
//                       <button type="button" className="btn btn-secondary btn-lg" onClick={resetFormToCreateMode} disabled={isLoading}>
//                         Cancel Edit
//                       </button>
//                     )}
//                   </div>
//                 </form>
//               </div>
//             </div>
//             <div className="job-list-container">
//               <h5 className="mb-3 text-secondary">Recently Posted Jobs</h5>
//               {isLoadingPostedJobs ? <div className="text-center mt-3"><div className="spinner-border text-primary"></div></div> :
//                postedJobsError ? <div className="alert alert-danger">{postedJobsError}</div> :
//                postedJobs.length === 0 ? <p className="text-muted text-center">No jobs posted yet.</p> :
//                [...postedJobs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((job) => (
//                 <div key={job.job_id} className="job-list-card">
//                   <div className="d-flex justify-content-between">
//                     <h6 className="card-title" title={job.jobtitle}>{job.jobtitle || 'No Title'}</h6>
//                     <span className={`job-status ${job.status === 'Active' ? 'active' : 'inactive'}`}>{job.status}</span>
//                   </div>
//                   <p className="job-id mb-2">ID: {job.job_id}</p>
//                   <div className="action-buttons">
//                     <button className="btn btn-edit" onClick={() => handleEditJob(job.job_id)} disabled={isLoading || (isEditing && editingJobId !== job.job_id)}>
//                         <i className="fas fa-edit"></i>
//                     </button>
//                     <button className={`btn btn-toggle ${job.status === 'Active' ? 'btn-deactivate' : 'btn-activate'}`} onClick={() => handleToggleJobStatus(job)} disabled={updatingStatusJobId === job.job_id || isLoading || isEditing}>
//                       {updatingStatusJobId === job.job_id ? <span className="spinner-border spinner-border-sm"></span> : job.status === 'Active' ? 'Deactivate' : 'Activate'}
//                     </button>
//                   </div>
//                   <p className="posted-date">Posted: {job.created_at ? new Date(job.created_at).toLocaleDateString() : 'N/A'}</p>
//                 </div>
//               ))}
//             </div>
//           </div>
//         </main>
//       </div>
//     </div>
//   );
// };

// export default Jobposting;

// import React, { useState, useEffect, useCallback } from "react";
// import axios from "axios";
// import Swal from "sweetalert2";
// import Sidebar from "./Sidebar";
// import DashNavbar from "./DashNavbar";
// import ReactQuill from 'react-quill';
// import 'react-quill/dist/quill.snow.css';
// import '../css/JobPosting.css';

// const stripHtml = (html) => {
//   if (!html || html === '<p><br></p>' || html === '<p></p>') return "";
//   const doc = new DOMParser().parseFromString(html, 'text/html');
//   return doc.body.textContent || "";
// };

// const quillModules = {
//   toolbar: [
//     [{ 'header': [1, 2, 3, 4, 5, 6, false] }], ['bold', 'italic', 'underline', 'strike'],
//     [{ 'list': 'ordered'}, { 'list': 'bullet' }], [{ 'indent': '-1'}, { 'indent': '+1' }],
//     ['link'], ['clean']
//   ],
// };

// const quillFormats = ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'indent', 'link'];

// const editApiBaseUrl = "https://raasbackend.vetrinahealthcare.com/";

// const Jobposting = () => {
//   // Form State
//   const [jobId, setJobId] = useState("JOB-1");
//   const [jobtitle, setJobTitle] = useState("");
//   const [description, setDescription] = useState("");
//   const [vacancies, setVacancies] = useState("");
//   const [experience, setExperience] = useState("");
//   const [jobLocationsString, setJobLocationsString] = useState("");
//   const [roleType, setRoleType] = useState("");
//   // --- NEW STATE for Posting Type ---
//   const [openingPostAs, setOpeningPostAs] = useState("");

//   // Error State
//   const [jobTitleError, setJobTitleError] = useState("");
//   const [vacanciesError, setVacanciesError] = useState("");
//   const [experienceError, setExperienceError] = useState("");
//   const [jobLocationError, setJobLocationError] = useState("");
//   const [roleTypeError, setRoleTypeError] = useState("");
//   const [descriptionError, setDescriptionError] = useState("");
//   // --- NEW ERROR STATE for Posting Type ---
//   const [openingPostAsError, setOpeningPostAsError] = useState("");

//   // Control State
//   const [isLoading, setIsLoading] = useState(false);
//   const [isEditing, setIsEditing] = useState(false);
//   const [editingJobId, setEditingJobId] = useState(null);

//   // Posted Jobs List State
//   const [postedJobs, setPostedJobs] = useState([]);
//   const [isLoadingPostedJobs, setIsLoadingPostedJobs] = useState(true);
//   const [postedJobsError, setPostedJobsError] = useState(null);
//   const [updatingStatusJobId, setUpdatingStatusJobId] = useState(null);

//   // Dropdown Options
//   const roleOptions = [
//     { value: "Full Time", label: "Full Time" }, { value: "Part Time", label: "Part Time" },
//     { value: "Contractual", label: "Contractual" }, { value: "Intern", label: "Intern" },
//   ];
//   // --- NEW OPTIONS for Posting Type ---
//   const postingTypeOptions = [
//     { value: "Job_Opening", label: "Job Opening" },
//     { value: "Business_Opportunity", label: "Business Opportunity" },
//     { value: "Consultancy_Opportunity", label: "Consultancy Opportunity" },
//   ];

//   const generateNextJobId = useCallback((lastJobId) => {
//     const match = lastJobId?.match(/\d+$/);
//     if (match) return `JOB-${parseInt(match[0], 10) + 1}`;
//     return "JOB-1";
//   }, []);

//   const fetchPostedJobs = useCallback(async () => {
//     setIsLoadingPostedJobs(true);
//     setPostedJobsError(null);
//     try {
//       const response = await axios.get(`${editApiBaseUrl}/jobpostings_list/`);
//       setPostedJobs(response.data || []);
//     } catch (error) {
//       setPostedJobsError("Failed to load jobs.");
//     } finally {
//       setIsLoadingPostedJobs(false);
//     }
//   }, []);

//   const resetFormToCreateMode = useCallback(async () => {
//     setIsEditing(false); setEditingJobId(null);
//     setJobTitle(""); setDescription(""); setVacancies(""); setExperience("");
//     setJobLocationsString(""); setRoleType(""); setOpeningPostAs(""); // Reset new field

//     // Clear all errors
//     setJobTitleError(""); setVacanciesError(""); setExperienceError("");
//     setJobLocationError(""); setRoleTypeError(""); setDescriptionError("");
//     setOpeningPostAsError(""); // Reset new field error

//     try {
//       const response = await axios.get(`${editApiBaseUrl}/last-job-id/`);
//       setJobId(generateNextJobId(response.data.job_id || "JOB-0"));
//     } catch {
//       setJobId(generateNextJobId(localStorage.getItem("lastJobId") || "JOB-0"));
//     }
//   }, [generateNextJobId]);

//   useEffect(() => {
//     if (!isEditing) {
//       resetFormToCreateMode();
//     }
//   }, [isEditing, resetFormToCreateMode]);

//   useEffect(() => {
//     fetchPostedJobs();
//   }, [fetchPostedJobs]);
  
//   const validateAllFields = () => {
//     const titleError = !jobtitle.trim() ? "Job Title is required." : "";
//     const vacanciesError = !vacancies || !/^\d+$/.test(vacancies) || parseInt(vacancies, 10) <= 0 ? "Vacancies must be a positive number." : "";
//     const experienceError = !experience.trim() ? "Experience is required." : "";
//     const locationError = !jobLocationsString.trim() ? "Job Location is required." : "";
//     const roleError = !roleType ? "Please select a role type." : "";
//     const descError = !stripHtml(description).trim() ? "Description is required." : "";
//     // --- NEW VALIDATION for Posting Type ---
//     const openingPostAsErr = !openingPostAs ? "Please select a posting type." : "";
    
//     setJobTitleError(titleError);
//     setVacanciesError(vacanciesError);
//     setExperienceError(experienceError);
//     setJobLocationError(locationError);
//     setRoleTypeError(roleError);
//     setDescriptionError(descError);
//     setOpeningPostAsError(openingPostAsErr); // Set new error
    
//     return !(titleError || vacanciesError || experienceError || locationError || roleError || descError || openingPostAsErr);
//   };

//   const handleDescriptionChange = (content) => {
//     setDescription(content);
//     if (descriptionError) {
//       validateAllFields();
//     }
//   };

//   const handleEditJob = async (jobIdToEdit) => {
//     document.querySelector('.job-form-container')?.scrollTo({ top: 0, behavior: 'smooth' });
//     setIsLoading(true);
//     try {
//       const response = await axios.get(`${editApiBaseUrl}/get_job_posting/${jobIdToEdit}/`);
//       const jobData = response.data;
//       if (jobData) {
//         setIsEditing(true); setEditingJobId(jobData.job_id); setJobId(jobData.job_id);
//         setJobTitle(jobData.jobtitle || ""); setDescription(jobData.description || "");
//         setVacancies(jobData.vacancies?.toString() || ""); setExperience(jobData.experience || "");
//         setJobLocationsString(jobData.job_location || ""); setRoleType(jobData.role_type || "");
//         // --- POPULATE NEW FIELD on edit ---
//         setOpeningPostAs(jobData.opening_post_as || "");

//         // Clear errors
//         setJobTitleError(""); setVacanciesError(""); setExperienceError("");
//         setJobLocationError(""); setRoleTypeError(""); setDescriptionError("");
//         setOpeningPostAsError("");
//       } else {
//         Swal.fire("Error", "Could not load job details.", "error");
//       }
//     } catch (error) {
//       Swal.fire("Load Error", "Failed to load job details for editing.", "error");
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!validateAllFields()) {
//       Swal.fire({ title: "Validation Error", text: "Please fix the errors before submitting.", icon: "warning", timer: 3000 });
//       return;
//     }
//     setIsLoading(true);

//     const jobData = {
//       jobtitle, description, vacancies: parseInt(vacancies), experience,
//       job_location: jobLocationsString, role_type: roleType,
//       // --- ADD NEW FIELD to payload ---
//       opening_post_as: openingPostAs, 
//     };

//     const url = isEditing ? `${editApiBaseUrl}/update_job_posting/${editingJobId}/` : `${editApiBaseUrl}/JobPosting_view/`;
//     const method = isEditing ? 'put' : 'post';
    
//     if (!isEditing) {
//         try {
//             const res = await axios.get(`${editApiBaseUrl}/last-job-id/`);
//             jobData.job_id = generateNextJobId(res.data.job_id || "JOB-0");
//         } catch {
//             jobData.job_id = generateNextJobId(localStorage.getItem("lastJobId") || "JOB-0");
//         }
//     }

//     try {
//         await axios[method](url, jobData);
//         if (!isEditing) localStorage.setItem("lastJobId", jobData.job_id);
//         await Swal.fire({ title: isEditing ? "Job Updated!" : "Job Posted!", icon: "success", timer: 1500, showConfirmButton: false });
//         fetchPostedJobs();
//         await resetFormToCreateMode();
//     } catch (error) {
//         Swal.fire("Error", `An error occurred while ${isEditing ? 'updating' : 'posting'} the job.`, "error");
//     } finally {
//         setIsLoading(false);
//     }
//   };

//   const handleToggleJobStatus = async (job) => {
//     const targetStatus = job.status === "Active" ? "Inactive" : "Active";
//     const action = targetStatus === "Active" ? "activate" : "deactivate";
//     setUpdatingStatusJobId(job.job_id);
//     try {
//       await axios.put(`${editApiBaseUrl}/jobpostings/${job.job_id}/${action}/`);
//       Swal.fire({ title: "Status Updated!", text: `Job ${job.job_id} is now ${targetStatus}.`, icon: "success", timer: 1500, showConfirmButton: false });
//       fetchPostedJobs();
//     } catch (error) {
//       Swal.fire("Update Error", `Failed to ${action} job.`, "error");
//     } finally {
//       setUpdatingStatusJobId(null);
//     }
//   };

//   return (
//     <div>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <main className="main">
//           <div className="job-posting-page-wrapper">
//             <div className="job-form-container">
//               <div className="job-posting-form-inner">
//                 <h2 className="gradient-text mb-4 text-center">
//                   {isEditing ? `Edit Job Posting` : "Job Posting Form"}
//                 </h2>
//                 <form onSubmit={handleSubmit} noValidate>
//                   <div className="mb-3">
//                     <label className="form-label">Job ID</label>
//                     <input type="text" className="form-control" value={jobId} readOnly style={{ backgroundColor: '#e9ecef' }} />
//                   </div>
//                   <div className="mb-3">
//                     <label className="form-label">Job Title</label>
//                     <input type="text" className={`form-control ${jobTitleError ? 'is-invalid' : ''}`} placeholder="Enter role (e.g., Software Engineer)" value={jobtitle} onChange={(e) => setJobTitle(e.target.value)} />
//                     {jobTitleError && <div className="invalid-feedback">{jobTitleError}</div>}
//                   </div>
//                   <div className="row mb-3">
//                     <div className="col-md-6">
//                       <label className="form-label">Vacancies</label>
//                       <input type="text" className={`form-control ${vacanciesError ? 'is-invalid' : ''}`} placeholder="Number of vacancies" value={vacancies} onChange={(e) => setVacancies(e.target.value)} />
//                       {vacanciesError && <div className="invalid-feedback">{vacanciesError}</div>}
//                     </div>
//                     <div className="col-md-6">
//                       <label className="form-label">Experience</label>
//                       <input type="text" className={`form-control ${experienceError ? 'is-invalid' : ''}`} placeholder="e.g., 0-1 years, 2+ years" value={experience} onChange={(e) => setExperience(e.target.value)} />
//                       {experienceError && <div className="invalid-feedback">{experienceError}</div>}
//                     </div>
//                   </div>
//                   <div className="row mb-3">
//                     <div className="col-md-6">
//                       <label className="form-label">Job Location(s)</label>
//                       <input type="text" className={`form-control ${jobLocationError ? 'is-invalid' : ''}`} placeholder="Locations, comma-separated" value={jobLocationsString} onChange={(e) => setJobLocationsString(e.target.value)} />
//                       {jobLocationError && <div className="invalid-feedback">{jobLocationError}</div>}
//                     </div>
//                     <div className="col-md-6">
//                       <label className="form-label">Role Type</label>
//                       <select className={`form-select ${roleTypeError ? 'is-invalid' : ''}`} value={roleType} onChange={(e) => setRoleType(e.target.value)}>
//                         <option value="" disabled>Choose role type...</option>
//                         {roleOptions.map((option) => (<option key={option.value} value={option.value}>{option.label}</option>))}
//                       </select>
//                       {roleTypeError && <div className="invalid-feedback">{roleTypeError}</div>}
//                     </div>
//                   </div>
//                    {/* --- NEW UI ELEMENT for Posting Type --- */}
//                    <div className="mb-3">
//                     <label className="form-label">Post As</label>
//                     <select
//                       className={`form-select ${openingPostAsError ? 'is-invalid' : ''}`}
//                       value={openingPostAs}
//                       onChange={(e) => setOpeningPostAs(e.target.value)}
//                       disabled={isEditing} // Cannot change the type when editing
//                       title={isEditing ? "Cannot change the posting type after creation." : "Select the type of post"}
//                     >
//                       <option value="" disabled>Choose posting type...</option>
//                       {postingTypeOptions.map((option) => (
//                         <option key={option.value} value={option.value}>
//                           {option.label}
//                         </option>
//                       ))}
//                     </select>
//                     {openingPostAsError && <div className="invalid-feedback">{openingPostAsError}</div>}
//                   </div>
//                   <div className="mb-3">
//                     <label className="form-label">Description</label>
//                     <ReactQuill theme="snow" value={description} onChange={handleDescriptionChange} modules={quillModules} formats={quillFormats} placeholder="Enter job description, responsibilities..." />
//                     {descriptionError && <div className="text-danger small mt-1">{descriptionError}</div>}
//                   </div>
//                   <div className="pt-3 d-grid gap-2">
//                     <button type="submit" className="btn btn-primary btn-lg" disabled={isLoading}>
//                       {isLoading ? (<><span className="spinner-border spinner-border-sm me-2"></span>{isEditing ? "Updating..." : "Posting..."}</>) : (isEditing ? "Update Job" : "Post Job")}
//                     </button>
//                     {isEditing && (
//                       <button type="button" className="btn btn-secondary btn-lg" onClick={resetFormToCreateMode} disabled={isLoading}>
//                         Cancel Edit
//                       </button>
//                     )}
//                   </div>
//                 </form>
//               </div>
//             </div>
//             <div className="job-list-container">
//               <h5 className="mb-3 text-secondary">Recently Posted Jobs</h5>
//               {isLoadingPostedJobs ? <div className="text-center mt-3"><div className="spinner-border text-primary"></div></div> :
//                postedJobsError ? <div className="alert alert-danger">{postedJobsError}</div> :
//                postedJobs.length === 0 ? <p className="text-muted text-center">No jobs posted yet.</p> :
//                [...postedJobs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((job) => (
//                 <div key={job.job_id} className="job-list-card">
//                   <div className="d-flex justify-content-between">
//                     <h6 className="card-title" title={job.jobtitle}>{job.jobtitle || 'No Title'}</h6>
//                     <span className={`job-status ${job.status === 'Active' ? 'active' : 'inactive'}`}>{job.status}</span>
//                   </div>
//                   <p className="job-id mb-2">ID: {job.job_id}</p>
//                   <div className="action-buttons">
//                     <button className="btn btn-edit" onClick={() => handleEditJob(job.job_id)} disabled={isLoading || (isEditing && editingJobId !== job.job_id)}>
//                         <i className="fas fa-edit"></i>
//                     </button>
//                     <button className={`btn btn-toggle ${job.status === 'Active' ? 'btn-deactivate' : 'btn-activate'}`} onClick={() => handleToggleJobStatus(job)} disabled={updatingStatusJobId === job.job_id || isLoading || isEditing}>
//                       {updatingStatusJobId === job.job_id ? <span className="spinner-border spinner-border-sm"></span> : job.status === 'Active' ? 'Deactivate' : 'Activate'}
//                     </button>
//                   </div>
//                   <p className="posted-date">Posted: {job.created_at ? new Date(job.created_at).toLocaleDateString() : 'N/A'}</p>
//                 </div>
//               ))}
//             </div>
//           </div>
//         </main>
//       </div>
//     </div>
//   );
// };

// export default Jobposting;

// import React, { useState, useEffect, useCallback } from "react";
// import axios from "axios";
// import Swal from "sweetalert2";
// import Sidebar from "./Sidebar";
// import DashNavbar from "./DashNavbar";
// import ReactQuill from 'react-quill';
// import 'react-quill/dist/quill.snow.css';
// import '../css/JobPosting.css'; // <-- FIX 1: Corrected CSS import path

// const stripHtml = (html) => {
//   if (!html || html === '<p><br></p>' || html === '<p></p>') return "";
//   const doc = new DOMParser().parseFromString(html, 'text/html');
//   return doc.body.textContent || "";
// };

// const quillModules = {
//   toolbar: [
//     [{ 'header': [1, 2, 3, 4, 5, 6, false] }], ['bold', 'italic', 'underline', 'strike'],
//     [{ 'list': 'ordered'}, { 'list': 'bullet' }], [{ 'indent': '-1'}, { 'indent': '+1' }],
//     ['link'], ['clean']
//   ],
// };

// const quillFormats = ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'indent', 'link'];

// const editApiBaseUrl = "https://raasbackend.vetrinahealthcare.com/";

// const Jobposting = () => {
//   const [jobId, setJobId] = useState("JOB-1");
//   const [jobtitle, setJobTitle] = useState("");
//   const [description, setDescription] = useState("");
//   const [vacancies, setVacancies] = useState("");
//   const [experience, setExperience] = useState("");
//   const [jobLocationsString, setJobLocationsString] = useState("");
//   const [roleType, setRoleType] = useState("");

//   const [jobTitleError, setJobTitleError] = useState("");
//   const [vacanciesError, setVacanciesError] = useState("");
//   const [experienceError, setExperienceError] = useState("");
//   const [jobLocationError, setJobLocationError] = useState("");
//   const [roleTypeError, setRoleTypeError] = useState("");
//   const [descriptionError, setDescriptionError] = useState("");

//   const [isLoading, setIsLoading] = useState(false);
//   const [isEditing, setIsEditing] = useState(false);
//   const [editingJobId, setEditingJobId] = useState(null);

//   const [postedJobs, setPostedJobs] = useState([]);
//   const [isLoadingPostedJobs, setIsLoadingPostedJobs] = useState(true);
//   const [postedJobsError, setPostedJobsError] = useState(null);
//   const [updatingStatusJobId, setUpdatingStatusJobId] = useState(null);

//   const roleOptions = [
//     { value: "Full Time", label: "Full Time" }, { value: "Part Time", label: "Part Time" },
//     { value: "Contractual", label: "Contractual" }, { value: "Intern", label: "Intern" },
//   ];

//   const generateNextJobId = useCallback((lastJobId) => {
//     const match = lastJobId?.match(/\d+$/);
//     if (match) return `JOB-${parseInt(match[0], 10) + 1}`;
//     return "JOB-1";
//   }, []);

//   const fetchPostedJobs = useCallback(async () => {
//     setIsLoadingPostedJobs(true);
//     setPostedJobsError(null);
//     try {
//       const response = await axios.get(`${editApiBaseUrl}/jobpostings_list/`);
//       setPostedJobs(response.data || []);
//     } catch (error) {
//       setPostedJobsError("Failed to load jobs.");
//     } finally {
//       setIsLoadingPostedJobs(false);
//     }
//   }, []);

//   const resetFormToCreateMode = useCallback(async () => {
//     setIsEditing(false); setEditingJobId(null);
//     setJobTitle(""); setDescription(""); setVacancies(""); setExperience("");
//     setJobLocationsString(""); setRoleType("");
//     setJobTitleError(""); setVacanciesError(""); setExperienceError("");
//     setJobLocationError(""); setRoleTypeError(""); setDescriptionError("");
//     try {
//       const response = await axios.get(`${editApiBaseUrl}/last-job-id/`);
//       setJobId(generateNextJobId(response.data.job_id || "JOB-0"));
//     } catch {
//       setJobId(generateNextJobId(localStorage.getItem("lastJobId") || "JOB-0"));
//     }
//   }, [generateNextJobId]);

//   useEffect(() => {
//     if (!isEditing) {
//       resetFormToCreateMode();
//     }
//   }, [isEditing, resetFormToCreateMode]);

//   useEffect(() => {
//     fetchPostedJobs();
//   }, [fetchPostedJobs]);
  
//   const validateAllFields = () => {
//     const titleError = !jobtitle.trim() ? "Job Title is required." : "";
//     const vacanciesError = !vacancies || !/^\d+$/.test(vacancies) || parseInt(vacancies, 10) <= 0 ? "Vacancies must be a positive number." : "";
//     const experienceError = !experience.trim() ? "Experience is required." : "";
//     const locationError = !jobLocationsString.trim() ? "Job Location is required." : "";
//     const roleError = !roleType ? "Please select a role type." : "";
//     const descError = !stripHtml(description).trim() ? "Description is required." : "";
    
//     setJobTitleError(titleError);
//     setVacanciesError(vacanciesError);
//     setExperienceError(experienceError);
//     setJobLocationError(locationError);
//     setRoleTypeError(roleError);
//     setDescriptionError(descError);
    
//     return !(titleError || vacanciesError || experienceError || locationError || roleError || descError);
//   };

//   // FIX 2: Re-added the missing handleDescriptionChange function
//   const handleDescriptionChange = (content) => {
//     setDescription(content);
//     // Optionally validate on change
//     if (descriptionError) {
//       validateAllFields();
//     }
//   };

//   const handleEditJob = async (jobIdToEdit) => {
//     document.querySelector('.job-form-container')?.scrollTo({ top: 0, behavior: 'smooth' });
//     setIsLoading(true);
//     try {
//       const response = await axios.get(`${editApiBaseUrl}/get_job_posting/${jobIdToEdit}/`);
//       const jobData = response.data;
//       if (jobData) {
//         setIsEditing(true); setEditingJobId(jobData.job_id); setJobId(jobData.job_id);
//         setJobTitle(jobData.jobtitle || ""); setDescription(jobData.description || "");
//         setVacancies(jobData.vacancies?.toString() || ""); setExperience(jobData.experience || "");
//         setJobLocationsString(jobData.job_location || ""); setRoleType(jobData.role_type || "");
//         setJobTitleError(""); setVacanciesError(""); setExperienceError("");
//         setJobLocationError(""); setRoleTypeError(""); setDescriptionError("");
//       } else {
//         Swal.fire("Error", "Could not load job details.", "error");
//       }
//     } catch (error) {
//       Swal.fire("Load Error", "Failed to load job details for editing.", "error");
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!validateAllFields()) {
//       Swal.fire({ title: "Validation Error", text: "Please fix the errors before submitting.", icon: "warning", timer: 3000 });
//       return;
//     }
//     setIsLoading(true);

//     const jobData = {
//       jobtitle, description, vacancies: parseInt(vacancies), experience,
//       job_location: jobLocationsString, role_type: roleType,
//     };

//     const url = isEditing ? `${editApiBaseUrl}/update_job_posting/${editingJobId}/` : `${editApiBaseUrl}/JobPosting_view/`;
//     const method = isEditing ? 'put' : 'post';
    
//     if (!isEditing) {
//         try {
//             const res = await axios.get(`${editApiBaseUrl}/last-job-id/`);
//             jobData.job_id = generateNextJobId(res.data.job_id || "JOB-0");
//         } catch {
//             jobData.job_id = generateNextJobId(localStorage.getItem("lastJobId") || "JOB-0");
//         }
//     }

//     try {
//         await axios[method](url, jobData);
//         if (!isEditing) localStorage.setItem("lastJobId", jobData.job_id);
//         await Swal.fire({ title: isEditing ? "Job Updated!" : "Job Posted!", icon: "success", timer: 1500, showConfirmButton: false });
//         fetchPostedJobs();
//         await resetFormToCreateMode();
//     } catch (error) {
//         Swal.fire("Error", `An error occurred while ${isEditing ? 'updating' : 'posting'} the job.`, "error");
//     } finally {
//         setIsLoading(false);
//     }
//   };

//   const handleToggleJobStatus = async (job) => {
//     const targetStatus = job.status === "Active" ? "Inactive" : "Active";
//     const action = targetStatus === "Active" ? "activate" : "deactivate";
//     setUpdatingStatusJobId(job.job_id);
//     try {
//       await axios.put(`${editApiBaseUrl}/jobpostings/${job.job_id}/${action}/`);
//       Swal.fire({ title: "Status Updated!", text: `Job ${job.job_id} is now ${targetStatus}.`, icon: "success", timer: 1500, showConfirmButton: false });
//       fetchPostedJobs();
//     } catch (error) {
//       Swal.fire("Update Error", `Failed to ${action} job.`, "error");
//     } finally {
//       setUpdatingStatusJobId(null);
//     }
//   };

//   return (
//     <div>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <main className="main">
//           <div className="job-posting-page-wrapper">
//             <div className="job-form-container">
//               <div className="job-posting-form-inner">
//                 <h2 className="gradient-text mb-4 text-center">
//                   {isEditing ? `Edit Job Posting` : "Job Posting Form"}
//                 </h2>
//                 <form onSubmit={handleSubmit} noValidate>
//                   <div className="mb-3">
//                     <label className="form-label">Job ID</label>
//                     <input type="text" className="form-control" value={jobId} readOnly style={{ backgroundColor: '#e9ecef' }} />
//                   </div>
//                   <div className="mb-3">
//                     <label className="form-label">Job Title</label>
//                     <input type="text" className={`form-control ${jobTitleError ? 'is-invalid' : ''}`} placeholder="Enter role (e.g., Software Engineer)" value={jobtitle} onChange={(e) => setJobTitle(e.target.value)} />
//                     {jobTitleError && <div className="invalid-feedback">{jobTitleError}</div>}
//                   </div>
//                   <div className="row mb-3">
//                     <div className="col-md-6">
//                       <label className="form-label">Vacancies</label>
//                       <input type="text" className={`form-control ${vacanciesError ? 'is-invalid' : ''}`} placeholder="Number of vacancies" value={vacancies} onChange={(e) => setVacancies(e.target.value)} />
//                       {vacanciesError && <div className="invalid-feedback">{vacanciesError}</div>}
//                     </div>
//                     <div className="col-md-6">
//                       <label className="form-label">Experience</label>
//                       <input type="text" className={`form-control ${experienceError ? 'is-invalid' : ''}`} placeholder="e.g., 0-1 years, 2+ years" value={experience} onChange={(e) => setExperience(e.target.value)} />
//                       {experienceError && <div className="invalid-feedback">{experienceError}</div>}
//                     </div>
//                   </div>
//                   <div className="row mb-3">
//                     <div className="col-md-6">
//                       <label className="form-label">Job Location(s)</label>
//                       <input type="text" className={`form-control ${jobLocationError ? 'is-invalid' : ''}`} placeholder="Locations, comma-separated" value={jobLocationsString} onChange={(e) => setJobLocationsString(e.target.value)} />
//                       {jobLocationError && <div className="invalid-feedback">{jobLocationError}</div>}
//                     </div>
//                     <div className="col-md-6">
//                       <label className="form-label">Role Type</label>
//                       <select className={`form-select ${roleTypeError ? 'is-invalid' : ''}`} value={roleType} onChange={(e) => setRoleType(e.target.value)}>
//                         <option value="" disabled>Choose...</option>
//                         {roleOptions.map((option) => (<option key={option.value} value={option.value}>{option.label}</option>))}
//                       </select>
//                       {roleTypeError && <div className="invalid-feedback">{roleTypeError}</div>}
//                     </div>
//                   </div>
//                   <div className="mb-3">
//                     <label className="form-label">Description</label>
//                     <ReactQuill theme="snow" value={description} onChange={handleDescriptionChange} modules={quillModules} formats={quillFormats} placeholder="Enter job description, responsibilities..." />
//                     {descriptionError && <div className="text-danger small mt-1">{descriptionError}</div>}
//                   </div>
//                   <div className="pt-3 d-grid gap-2">
//                     <button type="submit" className="btn btn-primary btn-lg" disabled={isLoading}>
//                       {isLoading ? (<><span className="spinner-border spinner-border-sm me-2"></span>{isEditing ? "Updating..." : "Posting..."}</>) : (isEditing ? "Update Job" : "Post Job")}
//                     </button>
//                     {isEditing && (
//                       <button type="button" className="btn btn-secondary btn-lg" onClick={resetFormToCreateMode} disabled={isLoading}>
//                         Cancel Edit
//                       </button>
//                     )}
//                   </div>
//                 </form>
//               </div>
//             </div>
//             <div className="job-list-container">
//               <h5 className="mb-3 text-secondary">Recently Posted Jobs</h5>
//               {isLoadingPostedJobs ? <div className="text-center mt-3"><div className="spinner-border text-primary"></div></div> :
//                postedJobsError ? <div className="alert alert-danger">{postedJobsError}</div> :
//                postedJobs.length === 0 ? <p className="text-muted text-center">No jobs posted yet.</p> :
//                [...postedJobs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((job) => (
//                 <div key={job.job_id} className="job-list-card">
//                   <div className="d-flex justify-content-between">
//                     <h6 className="card-title" title={job.jobtitle}>{job.jobtitle || 'No Title'}</h6>
//                     <span className={`job-status ${job.status === 'Active' ? 'active' : 'inactive'}`}>{job.status}</span>
//                   </div>
//                   <p className="job-id mb-2">ID: {job.job_id}</p>
//                   <div className="action-buttons">
//                     <button className="btn btn-edit" onClick={() => handleEditJob(job.job_id)} disabled={isLoading || (isEditing && editingJobId !== job.job_id)}>
//                         <i className="fas fa-edit"></i>
//                     </button>
//                     <button className={`btn btn-toggle ${job.status === 'Active' ? 'btn-deactivate' : 'btn-activate'}`} onClick={() => handleToggleJobStatus(job)} disabled={updatingStatusJobId === job.job_id || isLoading || isEditing}>
//                       {updatingStatusJobId === job.job_id ? <span className="spinner-border spinner-border-sm"></span> : job.status === 'Active' ? 'Deactivate' : 'Activate'}
//                     </button>
//                   </div>
//                   <p className="posted-date">Posted: {job.created_at ? new Date(job.created_at).toLocaleDateString() : 'N/A'}</p>
//                 </div>
//               ))}
//             </div>
//           </div>
//         </main>
//       </div>
//     </div>
//   );
// };

// export default Jobposting;

// import React, { useState, useEffect, useCallback } from "react";
// import axios from "axios";
// import Swal from "sweetalert2";
// import Sidebar from "./Sidebar"; // Assuming these exist
// import DashNavbar from "./DashNavbar"; // Assuming these exist
// import ReactQuill from 'react-quill';
// import 'react-quill/dist/quill.snow.css';

// // Helper function to strip HTML tags for validation purposes
// const stripHtml = (html) => {
//   if (!html || html === '<p><br></p>' || html === '<p></p>') {
//     return "";
//   }
//   try {
//     const doc = new DOMParser().parseFromString(html, 'text/html');
//     return doc.body.textContent || "";
//   } catch (e) {
//     console.error("Error parsing HTML for stripHtml:", e);
//     return html.replace(/<[^>]*>?/gm, '');
//   }
// };

// // Quill editor modules configuration
// const quillModules = {
//   toolbar: [
//     [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
//     ['bold', 'italic', 'underline', 'strike'],
//     [{ 'color': [] }, { 'background': [] }],
//     [{ 'list': 'ordered'}, { 'list': 'bullet' }],
//     [{ 'script': 'sub'}, { 'script': 'super' }],
//     [{ 'indent': '-1'}, { 'indent': '+1' }],
//     [{ 'direction': 'rtl' }],
//     [{ 'align': [] }],
//     ['link'],
//     ['clean']
//   ],
// };

// // Quill editor formats
// const quillFormats = [
//   'header',
//   'bold', 'italic', 'underline', 'strike',
//   'color', 'background',
//   'list', 'bullet',
//   'script',
//   'indent',
//   'direction',
//   'align',
//   'link',
// ];

// // API base URL for edit operations
// const editApiBaseUrl = "https://raasbackend.vetrinahealthcare.com/";

// const Jobposting = (props) => {
//   // --- State for Job Posting Form ---
//   const [jobId, setJobId] = useState("JOB-1");
//   const [jobtitle, setJobTitle] = useState("");
//   const [description, setDescription] = useState("");
//   const [vacancies, setVacancies] = useState("");
//   const [experience, setExperience] = useState("");
//   const [jobLocationsString, setJobLocationsString] = useState("");
//   const [roleType, setRoleType] = useState("");

//   // --- State for Form Validation ---
//   const [jobTitleError, setJobTitleError] = useState("");
//   const [vacanciesError, setVacanciesError] = useState("");
//   const [experienceError, setExperienceError] = useState("");
//   const [jobLocationError, setJobLocationError] = useState("");
//   const [roleTypeError, setRoleTypeError] = useState("");
//   const [descriptionError, setDescriptionError] = useState("");

//   // --- State for Form Submission & Editing ---
//   const [isLoading, setIsLoading] = useState(false);
//   const [isEditing, setIsEditing] = useState(false);
//   const [editingJobId, setEditingJobId] = useState(null);

//   // --- State for Recently Posted Jobs Stack ---
//   const [postedJobs, setPostedJobs] = useState([]);
//   const [isLoadingPostedJobs, setIsLoadingPostedJobs] = useState(true);
//   const [postedJobsError, setPostedJobsError] = useState(null);
//   const [updatingStatusJobId, setUpdatingStatusJobId] = useState(null);

//   const roleOptions = [
//     { value: "Full Time", label: "Full Time" },
//     { value: "Part Time", label: "Part Time" },
//     { value: "Contractual", label: "Contractual" },
//     { value: "Intern", label: "Intern" },
//   ];

//   const generateNextJobId = useCallback((lastJobId) => {
//     const match = lastJobId?.match(/\d+$/);
//     if (match) {
//       return `JOB-${parseInt(match[0], 10) + 1}`;
//     }
//     console.warn("Could not parse last Job ID:", lastJobId, "Defaulting to JOB-1");
//     return "JOB-1";
//   }, []);

//   const fetchPostedJobs = useCallback(async () => {
//     setIsLoadingPostedJobs(true);
//     setPostedJobsError(null);
//     try {
//       const postingsApiUrl = process.env.REACT_APP_POSTINGS_API_URL || "https://raasbackend.vetrinahealthcare.com/";
//       const response = await axios.get(`${postingsApiUrl}/jobpostings_list/`);
//       setPostedJobs(response.data || []);
//     } catch (error) {
//       console.error("Error fetching recently posted jobs:", error);
//       setPostedJobsError("Failed to load recently posted jobs. Ensure the API is running and returns the 'status' field for each job.");
//     } finally {
//       setIsLoadingPostedJobs(false);
//     }
//   }, []);

//   const resetFormToCreateMode = useCallback(async () => {
//     setIsEditing(false);
//     setEditingJobId(null);

//     setJobTitle("");
//     setDescription("");
//     setVacancies("");
//     setExperience("");
//     setJobLocationsString("");
//     setRoleType("");

//     setJobTitleError("");
//     setVacanciesError("");
//     setExperienceError("");
//     setJobLocationError("");
//     setRoleTypeError("");
//     setDescriptionError("");

//     // Fetch the next Job ID for a new post
//     try {
//         const apiUrl = process.env.REACT_APP_API_URL || "https://raasbackend.vetrinahealthcare.com/";
//         const response = await axios.get(`${apiUrl}/last-job-id/`);
//         const lastJobIdFetched = response.data.job_id || "JOB-0";
//         setJobId(generateNextJobId(lastJobIdFetched));
//     } catch (error) {
//         console.error("Error fetching last job ID for reset:", error);
//         const localLastJobId = localStorage.getItem("lastJobId") || "JOB-0";
//         setJobId(generateNextJobId(localLastJobId));
//     }
//   }, [generateNextJobId]);

//   useEffect(() => {
//     const fetchInitialOrNextJobId = async () => {
//       // This runs on mount (isEditing is false) or when isEditing becomes false (e.g., after cancel/successful edit)
//       if (!isEditing) {
//         try {
//           const apiUrl = process.env.REACT_APP_API_URL || "https://raasbackend.vetrinahealthcare.com/";
//           const response = await axios.get(`${apiUrl}/last-job-id/`);
//           const lastJobIdFetched = response.data.job_id || "JOB-0";
//           setJobId(generateNextJobId(lastJobIdFetched));
//         } catch (error) {
//           console.error("Error fetching initial/next job ID:", error);
//           const localLastJobId = localStorage.getItem("lastJobId") || "JOB-0";
//           setJobId(generateNextJobId(localLastJobId));
//         }
//       }
//     };
//     fetchInitialOrNextJobId();
//   }, [isEditing, generateNextJobId]);

//   useEffect(() => {
//     fetchPostedJobs();
//   }, [fetchPostedJobs]);

//   // --- Validation Functions ---
//   const validateJobTitle = (value) => {
//     const hasError = !value || !/^[A-Za-z0-9\s.,_&@#\-()/]*$/.test(value);
//     setJobTitleError(
//       !value ? "Job Title is required." :
//       hasError ? "Job Title contains invalid characters." : ""
//     );
//     return hasError;
//   };

//   const validateVacancies = (value) => {
//     const hasError = !value || !/^\d+$/.test(value) || parseInt(value) <= 0;
//     setVacanciesError(
//       !value ? "Vacancies is required." :
//       hasError ? "Vacancies must be a positive number." : ""
//     );
//     return hasError;
//   };

//   const validateExperience = (value) => {
//      const hasError = !value || !/^[0-9\-+\sA-Za-z]*(years?|Months?)?$/i.test(value);
//      setExperienceError(
//        !value ? "Experience is required." :
//        hasError ? "Invalid format (e.g., 2 years, 0-1 years, 5+ years, 6 Months)." : ""
//      );
//      return hasError;
//   };

//   const validateJobLocation = (value) => {
//     const hasError = !value || !/^[A-Za-z\s,]*$/.test(value);
//     setJobLocationError(
//       !value ? "Job Location is required." :
//       hasError ? "Location should only contain letters, spaces, and commas." : ""
//     );
//     return hasError;
//   };

//   const validateRoleType = (value) => {
//     const hasError = !value;
//     setRoleTypeError(hasError ? "Please select a role type." : "");
//     return hasError;
//   };

//   const validateDescription = (htmlContent) => {
//     const textContent = stripHtml(htmlContent);
//     const hasError = !textContent.trim();
//     setDescriptionError(hasError ? "Description is required." : "");
//     return hasError;
//   };

//   const handleDescriptionChange = (content) => {
//     setDescription(content);
//     validateDescription(content);
//   };

//   const handleEditJob = async (jobIdToEdit) => {
//     setIsLoading(true);
//     try {
//         const response = await axios.get(`${editApiBaseUrl}/get_job_posting/${jobIdToEdit}/`);
//         const jobData = response.data;

//         if (jobData) {
//             setIsEditing(true);
//             setEditingJobId(jobData.job_id);

//             setJobId(jobData.job_id);
//             setJobTitle(jobData.jobtitle || "");
//             setDescription(jobData.description || "");
//             setVacancies(jobData.vacancies?.toString() || "");
//             setExperience(jobData.experience || "");
//             setJobLocationsString(jobData.job_location || "");
//             setRoleType(jobData.role_type || "");

//             setJobTitleError("");
//             setVacanciesError("");
//             setExperienceError("");
//             setJobLocationError("");
//             setRoleTypeError("");
//             setDescriptionError("");
            
//             window.scrollTo({ top: 0, behavior: 'smooth' });
//         } else {
//             Swal.fire("Error", "Could not load job details for editing.", "error");
//         }
//     } catch (error) {
//         console.error("Error fetching job for editing:", error);
//         let errorMessage = "Failed to load job details for editing.";
//         if (error.response && error.response.data) {
//             const errorData = error.response.data;
//             errorMessage = errorData.detail || errorData.error || (typeof errorData === 'string' ? errorData : JSON.stringify(errorData));
//         }
//         Swal.fire("Load Error", errorMessage, "error");
//         // Don't reset to create mode here, user might want to retry or form might still have old data
//     } finally {
//         setIsLoading(false);
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     const titleHasError = validateJobTitle(jobtitle);
//     const vacanciesHasError = validateVacancies(vacancies);
//     const experienceHasError = validateExperience(experience);
//     const locationHasError = validateJobLocation(jobLocationsString);
//     const roleTypeHasError = validateRoleType(roleType);
//     const descriptionHasError = validateDescription(description);

//     const hasErrors = titleHasError || vacanciesHasError || experienceHasError || locationHasError || roleTypeHasError || descriptionHasError;

//     if (hasErrors) {
//       Swal.fire({
//         title: "Validation Error",
//         text: "Please fix the errors in the form before submitting.",
//         icon: "warning",
//         confirmButtonText: "OK",
//         timer: 3000,
//         timerProgressBar: true,
//       });
//       return;
//     }

//     setIsLoading(true);

//     if (isEditing && editingJobId) {
//         // --- UPDATE JOB ---
//         try {
//             await axios.put(`${editApiBaseUrl}/update_job_posting/${editingJobId}/`, {
//                 // job_id: editingJobId, // Not typically sent in body for PUT if in URL
//                 jobtitle,
//                 description,
//                 vacancies: parseInt(vacancies),
//                 experience,
//                 job_location: jobLocationsString,
//                 role_type: roleType,
//             });

//             await Swal.fire({
//                 title: "Job Updated!",
//                 text: `Job ${editingJobId} has been successfully updated.`,
//                 icon: "success",
//                 timer: 1500,
//                 timerProgressBar: true,
//                 showConfirmButton: false,
//             });
//             fetchPostedJobs();
//             await resetFormToCreateMode();
//         } catch (error) {
//             console.error("Error updating job:", error);
//             let errorMessage = "An error occurred while updating the job.";
//             if (error.response && error.response.data) {
//                 const errorData = error.response.data;
//                 if (errorData.error_message) errorMessage = errorData.error_message;
//                 else if (errorData.detail) errorMessage = errorData.detail;
//                 else if (errorData.error) errorMessage = errorData.error;
//                 else if (typeof errorData === 'object') {
//                      errorMessage = Object.entries(errorData).map(([field, messages]) =>
//                         `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`
//                     ).join(' | ');
//                 } else if (typeof errorData === 'string') errorMessage = errorData;
//             }
//             Swal.fire("Update Error", errorMessage, "error");
//         } finally {
//             setIsLoading(false);
//         }
//     } else {
//         // --- CREATE NEW JOB ---
//         let currentJobIdToPost = jobId; // Use jobId from state, which should be the next new ID
//          try {
//             const apiUrl = process.env.REACT_APP_API_URL || "https://raasbackend.vetrinahealthcare.com/";
//             try {
//                 const response = await axios.get(`${apiUrl}/last-job-id/`);
//                 const lastJobIdFetched = response.data.job_id || "JOB-0";
//                 currentJobIdToPost = generateNextJobId(lastJobIdFetched);
//             } catch (fetchError) {
//                 console.warn("Error fetching latest job ID before submit, using current state ID:", fetchError);
//                 const localLastJobId = localStorage.getItem("lastJobId") || "JOB-0";
//                 currentJobIdToPost = generateNextJobId(localLastJobId);
//             }
//             // setJobId(currentJobIdToPost); // This will be handled by resetFormToCreateMode

//             const postApiUrl = process.env.REACT_APP_POSTINGS_API_URL || "https://raasbackend.vetrinahealthcare.com/";
//             await axios.post(`${postApiUrl}/JobPosting_view/`, {
//                 job_id: currentJobIdToPost,
//                 jobtitle,
//                 description,
//                 vacancies: parseInt(vacancies),
//                 experience,
//                 job_location: jobLocationsString,
//                 role_type: roleType,
//             });

//             localStorage.setItem("lastJobId", currentJobIdToPost);
//             await Swal.fire({
//                 title: "Job Posted!",
//                 text: `Job ${currentJobIdToPost} has been successfully posted.`,
//                 icon: "success",
//                 timer: 1500,
//                 timerProgressBar: true,
//                 showConfirmButton: false,
//             });
//             fetchPostedJobs();
//             await resetFormToCreateMode();
//         } catch (error) {
//             console.error("Error posting job:", error);
//             let errorMessage = "An error occurred while posting the job.";
//             if (error.response && error.response.data) {
//                 const errorData = error.response.data;
//                 if (typeof errorData === 'string') errorMessage = errorData;
//                 else if (errorData.error) errorMessage = errorData.error;
//                 else if (errorData.detail) errorMessage = errorData.detail;
//                 else if (typeof errorData === 'object') {
//                     const fieldErrors = Object.entries(errorData).map(([field, messages]) =>
//                         `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`
//                     ).join(' | ');
//                     if (fieldErrors) errorMessage = fieldErrors;
//                 }
//             }
//             Swal.fire("Post Error", errorMessage, "error");
//         } finally {
//             setIsLoading(false);
//         }
//     }
//   };

//   const handleToggleJobStatus = async (jobToUpdate) => {
//     if (!jobToUpdate || !jobToUpdate.job_id || typeof jobToUpdate.status === 'undefined') {
//         console.error("Invalid job data for status toggle:", jobToUpdate);
//         Swal.fire("Error", "Cannot update status: Invalid job data or status missing.", "error");
//         return;
//     }

//     const currentStatus = jobToUpdate.status;
//     const targetStatus = currentStatus === "Active" ? "Inactive" : "Active";
//     const action = targetStatus === "Active" ? "activate" : "deactivate";
//     const jobIdToUpdate = jobToUpdate.job_id;

//     setUpdatingStatusJobId(jobIdToUpdate);

//     try {
//       const apiUrl = process.env.REACT_APP_API_URL || "https://raasbackend.vetrinahealthcare.com/";
//       await axios.put(`${apiUrl}/jobpostings/${jobIdToUpdate}/${action}/`);

//       Swal.fire({
//         title: "Status Updated!",
//         text: `Job ${jobIdToUpdate} has been ${targetStatus.toLowerCase()}d.`,
//         icon: "success",
//         timer: 1500,
//         timerProgressBar: true,
//         showConfirmButton: false,
//       });
//       fetchPostedJobs();
//     } catch (error) {
//       console.error(`Error ${action}ing job ${jobIdToUpdate}:`, error);
//       let errorMessage = `Failed to ${action} job.`;
//       if (error.response && error.response.data) {
//         const errorData = error.response.data;
//         if (errorData.error) errorMessage = errorData.error;
//         else if (errorData.detail) errorMessage = errorData.detail;
//         else if (typeof errorData === 'object' && Object.keys(errorData).length > 0) {
//           errorMessage = JSON.stringify(errorData);
//         } else if (typeof errorData === 'string') {
//           errorMessage = errorData;
//         }
//       }
//       Swal.fire("Update Error", errorMessage, "error");
//     } finally {
//       setUpdatingStatusJobId(null);
//     }
//   };

//   return (
//     <div>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main" style={{ display: "flex", width: "100%", minHeight: 'calc(100vh - 60px)' }}>
//           {/* Left side: Job Posting Form */}
//           <div style={{ flex: "3 1 0%", padding: "20px", overflowY: 'auto', borderRight: '1px solid #e0e0e0' }}>
//             <div className="row justify-content-center " style={{ width: "100%" }}>
//               <div className="col-md-11 col-lg-10 col-xl-9 auth-inner mt-3 mb-5">
//                 <h2 className="display-6 gradient-text mb-4 ">
//                     {isEditing ? `Edit Job: ${editingJobId}` : "Job Posting Form"}
//                 </h2>
//                 <form onSubmit={handleSubmit} noValidate>
//                   <div className="mb-3">
//                     <label className="form-label text-start">Job ID:</label>
//                     <input
//                       type="text"
//                       className="form-control"
//                       value={jobId}
//                       readOnly
//                       style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
//                     />
//                   </div>

//                   <div className="mb-3">
//                     <label className="form-label text-start">Job Title:</label>
//                     <input
//                       type="text"
//                       name="jobtitle"
//                       className={`form-control ${jobTitleError ? 'is-invalid' : ''}`}
//                       placeholder="Enter role (e.g., Software Engineer)"
//                       value={jobtitle}
//                       onChange={(e) => { setJobTitle(e.target.value); validateJobTitle(e.target.value); }}
//                       required
//                     />
//                     {jobTitleError && <div className="invalid-feedback d-block">{jobTitleError}</div>}
//                   </div>

//                   <div className="row mb-3">
//                     <div className="col-md-6">
//                       <label className="form-label text-start">Vacancies:</label>
//                       <input
//                         type="number"
//                         name="vacancies"
//                         className={`form-control ${vacanciesError ? 'is-invalid' : ''}`}
//                         placeholder="Number of vacancies"
//                         value={vacancies}
//                         min="1"
//                         onChange={(e) => { setVacancies(e.target.value); validateVacancies(e.target.value); }}
//                         required
//                       />
//                       {vacanciesError && <div className="invalid-feedback d-block">{vacanciesError}</div>}
//                     </div>
//                     <div className="col-md-6">
//                       <label className="form-label text-start">Experience:</label>
//                       <input
//                         type="text"
//                         name="experience"
//                         className={`form-control ${experienceError ? 'is-invalid' : ''}`}
//                         placeholder="e.g., 0-1 years, 2+ years"
//                         value={experience}
//                         onChange={(e) => { setExperience(e.target.value); validateExperience(e.target.value); }}
//                         required
//                       />
//                       {experienceError && <div className="invalid-feedback d-block">{experienceError}</div>}
//                     </div>
//                   </div>

//                   <div className="row mb-3">
//                      <div className="col-md-6">
//                         <label className="form-label text-start">Job Location(s):</label>
//                         <input
//                           type="text"
//                           name="jobLocations"
//                           className={`form-control ${jobLocationError ? 'is-invalid' : ''}`}
//                           placeholder="Locations, comma-separated"
//                           value={jobLocationsString}
//                           onChange={(e) => { setJobLocationsString(e.target.value); validateJobLocation(e.target.value); }}
//                           required
//                         />
//                         {jobLocationError && <div className="invalid-feedback d-block">{jobLocationError}</div>}
//                      </div>
//                      <div className="col-md-6">
//                         <label className="form-label text-start">Role Type:</label>
//                         <select
//                           className={`form-select ${roleTypeError ? 'is-invalid' : ''}`}
//                           value={roleType}
//                           onChange={(e) => { setRoleType(e.target.value); validateRoleType(e.target.value); }}
//                           required
//                         >
//                           <option value="" disabled>Choose...</option>
//                           {roleOptions.map((option) => (
//                             <option key={option.value} value={option.value}>
//                               {option.label}
//                             </option>
//                           ))}
//                         </select>
//                         {roleTypeError && <div className="invalid-feedback d-block">{roleTypeError}</div>}
//                      </div>
//                   </div>

//                   <div className="mb-3">
//                      <label className="form-label text-start">Description:</label>
//                      <ReactQuill
//                         theme="snow"
//                         value={description}
//                         onChange={handleDescriptionChange}
//                         modules={quillModules}
//                         formats={quillFormats}
//                         placeholder="Enter job description, responsibilities, qualifications..."
//                         style={{ backgroundColor: 'white', minHeight: '200px' }}
//                       />
//                       {descriptionError && <div className="invalid-feedback d-block mt-1">{descriptionError}</div>}
//                   </div>

//                   <div className="pt-3">
//                     <button
//                       type="submit"
//                       className="btn btn-primary w-100 btn-lg"
//                       disabled={isLoading}
//                     >
//                       {isLoading ? (
//                         <>
//                           <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
//                           {isEditing ? "Updating..." : "Posting..."}
//                         </>
//                       ) : (
//                         isEditing ? "Update Job" : "Post Job"
//                       )}
//                     </button>
//                   </div>
//                   {isEditing && (
//                     <div className="pt-2">
//                         <button
//                             type="button"
//                             className="btn btn-secondary w-100 btn-lg"
//                             onClick={async () => {
//                                 setIsLoading(true); // Indicate loading while resetting
//                                 await resetFormToCreateMode();
//                                 setIsLoading(false);
//                             }}
//                             disabled={isLoading}
//                         >
//                             Cancel Edit
//                         </button>
//                     </div>
//                   )}
//                 </form>
//               </div>
//             </div>
//           </div>

//           {/* Right side: Recently Posted Jobs Stack */}
//           <div style={{ flex: "1 1 0%", padding: "20px 15px", overflowY: "auto", maxHeight: 'calc(100vh - 60px)', backgroundColor: '#f8f9fa' }}>
//             <h5 className="mb-3 text-secondary">Recently Posted Jobs</h5>
//             {isLoadingPostedJobs && <div className="text-center mt-3"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>}
//             {postedJobsError && <div className="alert alert-danger small mt-3">{postedJobsError}</div>}
//             {!isLoadingPostedJobs && !postedJobsError && (
//               <>
//                 {postedJobs.length === 0 ? (
//                   <p className="text-muted small mt-3 text-center">No recently posted jobs found.</p>
//                 ) : (
//                   postedJobs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 30).map((job) => (
//                     <div key={job.job_id} className="card-job mb-2 shadow-sm">
//                       <div className="card-body p-2">
//                         <div className="d-flex justify-content-between align-items-start">
//                             <div>
//                                 <h6 className="card-title mb-1" style={{fontSize: '0.95rem'}} title={job.jobtitle}>
//                                     {job.jobtitle ? (job.jobtitle.length > 30 ? job.jobtitle.substring(0, 27) + "..." : job.jobtitle) : 'No Title'}
//                                 </h6>
//                                 <small className="text-muted d-block" style={{fontSize: '0.8rem'}}>ID: {job.job_id}</small>
//                             </div>
//                             <span
//                                 className={`badge ms-2 ${job.status === 'Active' ? 'bg-success-subtle text-success-emphasis' : 'bg-secondary-subtle text-secondary-emphasis'}`}
//                                 style={{fontSize: '0.75rem', alignSelf: 'center'}}
//                             >
//                                 {job.status || 'N/A'}
//                             </span>
//                         </div>
//                         <div className="mt-2 d-flex justify-content-end align-items-center">
//                            <button
//                               className="btn btn-sm btn-outline-info me-1"
//                               onClick={() => handleEditJob(job.job_id)}
//                               disabled={updatingStatusJobId === job.job_id || isLoading || (isEditing && editingJobId !== job.job_id)}
//                               style={{fontSize: '0.8rem', padding: '0.15rem 0.4rem'}}
//                               title="Edit Job"
//                             >
//                               <i className="fas fa-edit"></i> {/* Ensure Font Awesome is linked in your public/index.html */}
//                             </button>
//                            <button
//                               className={`btn btn-sm ${job.status === 'Active' ? 'btn-outline-warning' : 'btn-outline-success'}`}
//                               onClick={() => handleToggleJobStatus(job)}
//                               disabled={updatingStatusJobId === job.job_id || isLoading || isEditing}
//                               style={{fontSize: '0.8rem', padding: '0.15rem 0.4rem'}}
//                             >
//                               {updatingStatusJobId === job.job_id ? (
//                                 <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
//                               ) : (
//                                 job.status === 'Active' ? 'Deactivate' : 'Activate'
//                               )}
//                             </button>
//                         </div>
//                          <small className="text-muted d-block mt-1" style={{fontSize: '0.75rem'}}>
//                             Posted: {job.created_at ? new Date(job.created_at).toLocaleDateString() : 'N/A'}
//                         </small>
//                       </div>
//                     </div>
//                   ))
//                 )}
//               </>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Jobposting;

// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import Swal from "sweetalert2";
// import Sidebar from "./Sidebar";
// import DashNavbar from "./DashNavbar";

// const Jobposting = (props) => {
//   const [jobId, setJobId] = useState("JOB-1");
//   const [jobtitle, setJobTitle] = useState("");
//   const [description, setDescription] = useState("");
//   const [vacancies, setVacancies] = useState("");
//   const [experience, setExperience] = useState("");
//   const [jobLocationsString, setJobLocationsString] = useState(""); // Comma-separated string
//   const [roleType, setRoleType] = useState("");

//   const [jobIdError, setJobIdError] = useState("");
//   const [jobTitleError, setJobTitleError] = useState("");
//   const [vacanciesError, setVacanciesError] = useState("");
//   const [experienceError, setExperienceError] = useState("");
//   const [jobLocationError, setJobLocationError] = useState("");
//   const [roleTypeError, setRoleTypeError] = useState("");
//   const [descriptionError, setDescriptionError] = useState("");

//   const [isLoading, setIsLoading] = useState(false);

//   const roleOptions = [
//     { value: "Full Time", label: "Full Time" },
//     { value: "Part Time", label: "Part Time" },
//     { value: "Contractual", label: "Contractual" },
//     { value: "Intern", label: "Intern" },
//   ];

//   useEffect(() => {
//     const fetchLastJobId = async () => {
//       try {
//         const response = await axios.get("https://raasbackend.vetrinahealthcare.com/last-job-id/");
//         const lastJobId = response.data.job_id || "JOB-0";
//         const nextJobId = generateNextJobId(lastJobId);
//         setJobId(nextJobId);
//       } catch (error) {
//         console.error("Error fetching last job ID:", error);
//         setJobId("JOB-1"); // Default if API fails
//       }
//     };

//     fetchLastJobId();
//   }, []);

//   const generateNextJobId = (lastJobId) => {
//     const match = lastJobId.match(/\d+$/);
//     if (match) {
//       return `JOB-${parseInt(match[0], 10) + 1}`;
//     }
//     return "JOB-1";
//   };

//   const handleRoleChange = (e) => {
//     setRoleType(e.target.value);
//   };

//   const validateJobId = (value) => {
//     if (!/^\d+$/.test(value)) {
//       setJobIdError("Please enter a valid Job ID.");
//     } else {
//       setJobIdError("");
//     }
//   };

//   const validateJobTitle = (value) => {
//     if (!/^[A-Za-z\s]*$/.test(value)) {
//       setJobTitleError("Job Title should only contain letters.");
//     } else {
//       setJobTitleError("");
//     }
//   };

//   const validateVacancies = (value) => {
//     if (!/^\d+$/.test(value)) {
//       setVacanciesError("Vacancies must be a valid number.");
//     } else {
//       setVacanciesError("");
//     }
//   };

//   const validateExperience = (value) => {
//     if (!/^[0-9.-]*(\s*years)?$/.test(value)) {
//       setExperienceError(
//         "Experience should be in a valid format (e.g., 1 year or 0-2 years)."
//       );
//     } else {
//       setExperienceError("");
//     }
//   };

//   const validateJobLocation = (value) => {
//     if (!/^[A-Za-z\s,]*$/.test(value)) { // Allow commas
//       setJobLocationError("Job Location should only contain letters and commas.");
//     } else {
//       setJobLocationError("");
//     }
//   };

//   const validateRoleType = (value) => {
//     if (!value) {
//       setRoleTypeError("Please select a role type.");
//     } else {
//       setRoleTypeError("");
//     }
//   };

//   const validateDescription = (value) => {
//     if (!value) {
//       setDescriptionError("Please enter a job description.");
//     } else {
//       setDescriptionError("");
//     }
//   };

//   const generateJobId = () => {
//     const lastJobId = localStorage.getItem("lastJobId") || "JOB-0";
//     console.log("Last Job ID from Storage:", lastJobId);

//     const jobNumber = parseInt(lastJobId.split("-")[1]) + 1;
//     const newJobId = `JOB-${jobNumber}`;

//     localStorage.setItem("lastJobId", newJobId);
//     console.log("New Generated Job ID:", newJobId);

//     return newJobId;
//   };


//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (
//       jobTitleError ||
//       vacanciesError ||
//       experienceError ||
//       jobLocationError ||
//       roleTypeError ||
//       descriptionError
//     ) {
//       return;
//     }

//     setIsLoading(true);

//     try {
//       const newJobId = generateJobId();
//       setJobId(newJobId);

//       const jobLocationsArray = jobLocationsString.split(",").map(location => location.trim()).filter(location => location !== ""); // Convert string to array

//       const response = await axios.post("https://raasbackend.vetrinahealthcare.com/JobPosting_view/", {
//         job_id: newJobId,
//         jobtitle,
//         description,
//         vacancies,
//         experience,
//         job_location: jobLocationsArray, // Send as array
//         role_type: roleType,
//       });


//       await Swal.fire({
//         title: "Job Posted!",
//         text: "Your job has been successfully posted.",
//         icon: "success",
//         confirmButtonText: "OK",
//         timer: 1000,
//         timerProgressBar: true,
//         showConfirmButton: false,
//       });

//       console.log(response.data);

//       setJobTitle("");
//       setDescription("");
//       setVacancies("");
//       setExperience("");
//       setJobLocationsString(""); // Clear the string
//       setRoleType("");
//       setJobId(newJobId);
//     } catch (error) {
//       console.error("Error posting job:", error);
//       Swal.fire({
//         title: "Error",
//         text: "An error occurred while posting the job.",
//         icon: "error",
//         confirmButtonText: "OK",
//         timer: 4000,
//         timerProgressBar: true,
//         showConfirmButton: false,
//       });
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   return (
//     <div>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           <div
//             className="row justify-content-center "
//             style={{ width: "100%" }}
//           >
//             <div className="col-md-8 col-9 auth-inner mt-3 mb-5">
//               <h2 className="display-6 gradient-text mb-4 ">Job Posting Form</h2>
//               <form onSubmit={handleSubmit}>
//                 <div className="mb-3">
//                   <div className="row justify-content-center align-items-center">
//                     <div className="col-md-6">
//                       <label className="form-label text-end pe-3">Job ID:</label>
//                       <div className="input-container">
//                         <input
//                           type="text"
//                           name="text"
//                           className="input"
//                           placeholder="Auto-generated Job ID"
//                           value={jobId}
//                           readOnly
//                         />

//                         <div className="highlight"></div>
//                         {jobIdError && (
//                           <div className="text-danger">{jobIdError}</div>
//                         )}
//                       </div>
//                     </div>
//                     <div className="col-md-6">
//                       <label className="form-label text-end pe-3">
//                         Job Title:
//                       </label>
//                       <div className="input-container">
//                         <input
//                           type="text"
//                           name="text"
//                           className="input"
//                           placeholder="Enter role"
//                           value={jobtitle}
//                           onChange={(e) => {
//                             setJobTitle(e.target.value);
//                             validateJobTitle(e.target.value);
//                           }}
//                           required
//                         />
//                         <div className="highlight"></div>
//                         {jobTitleError && (
//                           <div className="text-danger">{jobTitleError}</div>
//                         )}
//                       </div>
//                     </div>
//                   </div>
//                 </div>

//                 <div className="mb-3">
//                   <div className="row justify-content-center align-items-center">
//                     <div className="col-md-6">
//                       <label className="form-label text-end pe-3">
//                         Vacancies:
//                       </label>
//                       <div className="input-container">
//                         <input
//                           type="text"
//                           name="text"
//                           className="input"
//                           placeholder="Enter vacancies"
//                           value={vacancies}
//                           onChange={(e) => {
//                             setVacancies(e.target.value);
//                             validateVacancies(e.target.value);
//                           }}
//                           required
//                         />
//                         <div className="highlight"></div>
//                         {vacanciesError && (
//                           <div className="text-danger">{vacanciesError}</div>
//                         )}
//                       </div>
//                     </div>
//                     <div className="col-md-6">
//                       <label className="form-label text-end pe-3">
//                         Experience:
//                       </label>
//                       <div className="input-container">
//                         <input
//                           type="text"
//                           name="text"
//                           className="input"
//                           placeholder="Enter number (e.g., 0-1 year)"
//                           value={experience}
//                           onChange={(e) => {
//                             setExperience(e.target.value);
//                             validateExperience(e.target.value);
//                           }}
//                           required
//                         />
//                         <div className="highlight"></div>
//                         {experienceError && (
//                           <div className="text-danger">{experienceError}</div>
//                         )}
//                       </div>
//                     </div>
//                   </div>
//                 </div>

//                 <div className="mb-3">
//                   <div className="row justify-content-center align-items-center">
//                     <div className="col-md-6">
//                       <label className="form-label text-end pe-3">
//                         Job Location (Comma-Separated):
//                       </label>
//                       <div className="input-container">
//                         <input
//                           type="text"
//                           name="text"
//                           className="input"
//                           placeholder="Enter locations separated by commas"
//                           value={jobLocationsString}
//                           onChange={(e) => {
//                             setJobLocationsString(e.target.value);
//                             validateJobLocation(e.target.value);
//                           }}
//                           required
//                         />
//                         <div className="highlight"></div>
//                         {jobLocationError && (
//                           <div className="text-danger">{jobLocationError}</div>
//                         )}
//                       </div>
//                     </div>
//                     <div className="col-md-6">
//                       <label className="form-label text-end pe-3">
//                         Role Type:
//                       </label>
//                       <div className="input-container">
//                         <select
//                           className="input"
//                           id="autoSizingSelect"
//                           value={roleType}
//                           onChange={(e) => {
//                             setRoleType(e.target.value);
//                             validateRoleType(e.target.value);
//                           }}
//                           required
//                         >
//                           <option value="" disabled>
//                             Choose...
//                           </option>
//                           {roleOptions.map((option) => (
//                             <option key={option.value} value={option.value}>
//                               {option.label}
//                             </option>
//                           ))}
//                         </select>
//                         <div className="highlight"></div>
//                         {roleTypeError && (
//                           <div className="text-danger">{roleTypeError}</div>
//                         )}
//                       </div>
//                     </div>
//                   </div>
//                 </div>

//                 <div className="mb-3">
//                   <div className="row justify-content-center align-items-center">
//                     <div className="col-md-8">
//                       <label className="form-label text-end pe-3">
//                         Description:
//                       </label>
//                       <div className="input-container">
//                         <textarea
//                           className="input"
//                           value={description}
//                           onChange={(e) => {
//                             setDescription(e.target.value);
//                             validateDescription(e.target.value);
//                           }}
//                           required
//                         />
//                         <div className="highlight"></div>
//                         {descriptionError && (
//                           <div className="text-danger">{descriptionError}</div>
//                         )}
//                       </div>
//                     </div>
//                   </div>
//                 </div>

//                 <div className="pt-3">
//                   <div className="row justify-content-center ">
//                     <div className="col-md-6 ">
//                       <button
//                         type="submit"
//                         className="btn btn-primary w-100"
//                         disabled={isLoading}
//                       >
//                         {isLoading ? (
//                           <span
//                             className="spinner-border spinner-border-sm"
//                             role="status"
//                             aria-hidden="true"
//                           ></span>
//                         ) : (
//                           "Post"
//                         )}
//                       </button>
//                     </div>
//                   </div>
//                 </div>
//               </form>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Jobposting;


// import React, { useState } from "react";
// import axios from "axios";
// import Swal from "sweetalert2";
// import Sidebar from "./Sidebar";
// import DashNavbar from "./DashNavbar";

// const Jobposting = (props) => {
//   const [jobId, setJobId] = useState("");
//   const [jobtitle, setJobTitle] = useState("");
//   const [description, setDescription] = useState("");
//   const [vacancies, setVacancies] = useState("");
//   const [experience, setExperience] = useState("");
//   const [jobLocation, setJobLocation] = useState("");
//   const [roleType, setRoleType] = useState("");
//   const roleOptions = [
//     { value: "Full Time", label: "Full Time" },
//     { value: "Part Time", label: "Part Time" },
//     { value: "Contractual", label: "Contractual" },
//     { value: "Intern", label: "Intern" },
//   ];
//   const [isLoading, setIsLoading] = useState(false);

//   const [newJobCount, setNewJobCount] = useState(0);
//   const handleRoleChange = (e) => {
//     const selectedValue = e.target.value;
//     setRoleType(selectedValue);
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     props.settingInfo(
//       jobId,
//       jobtitle,
//       description,
//       vacancies,
//       experience,
//       jobLocation,
//       roleType
//     );
//     setIsLoading(true); // Start loading

//     try {
//       const response = await axios.post("https://raasbackend.vetrinahealthcare.com/JobPosting_view/",
//         {
//           job_id: jobId,
//           jobtitle,
//           description,
//           vacancies,
//           experience,
//           job_location: jobLocation,
//           role_type: roleType,
//         }
//       );

//       // Increment new job count
//       setNewJobCount(newJobCount + 1);

//       await Swal.fire({
//         title: "Job Posted!",
//         text: "Your job has been successfully posted.",
//         icon: "success",
//         confirmButtonText: "OK",
//         timer: 1000,
//         timerProgressBar: true,
//         showConfirmButton: false,
//       });

//       console.log(response.data);

//       setJobTitle("");
//       setDescription("");
//       setVacancies("");
//       setExperience("");
//       setJobLocation("");
//       setRoleType("");
//     } catch (error) {
//       console.error("Error posting job:", error);

//       Swal.fire({
//         title: "Error",
//         text: "An error occurred while posting the job.",
//         icon: "error",
//         confirmButtonText: "OK",
//         timer: 4000,
//         timerProgressBar: true,
//         showConfirmButton: false,
//       });
//     } finally {
//       // Stop the loading spinner
//       setIsLoading(false);
//     }
//   };

//   return (
//     <div>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           <div
//             className="row justify-content-center "
//             style={{ width: "100%" }}
//           >
//             <div className="col-md-8 col-9 auth-inner mt-3 mb-5">
//               <h2 className="display-6 gradient-text mb-4 ">
//                 Job Posting Form
//               </h2>
//               <form onSubmit={handleSubmit}>
//                 <div className="mb-3">
//                   <div className="row justify-content-center align-items-center">
//                     <div className="col-md-6">
//                       <label className="form-label text-end pe-3">
//                         Job ID:
//                       </label>
//                       <div class="input-container">
//                         <input type="text" name="text" className="input" placeholder="Enter Job Id (number)"
//                           value={jobId}
//                           pattern="[0-9]*"
//                           onChange={(e) => setJobId(e.target.value)}
//                           required />
//                         <div class="highlight"></div>
//                       </div>

//                       {/* <input
//                         type="text"
//                         className="form-control"
//                         value={jobId}
//                         onChange={(e) => setJobId(e.target.value)}
//                         required
//                       /> */}
//                     </div>
//                     <div className="col-md-6">
//                       <label className="form-label text-end pe-3">
//                         Job Title:
//                       </label>
//                       <div class="input-container">
//                         <input type="text" name="text" className="input" placeholder="Enter role"
//                           value={jobtitle}
//                           onChange={(e) => setJobTitle(e.target.value)}
//                           required
//                         />
//                         <div class="highlight"></div>
//                       </div>
//                       {/* <input
//                         type="text"
//                         className="form-control"
//                         value={jobtitle}
//                         onChange={(e) => setJobTitle(e.target.value)}
//                         required
//                       /> */}
//                     </div>
//                   </div>
//                 </div>

//                 <div className="mb-3">
//                   <div className="row justify-content-center align-items-center">
//                     <div className="col-md-6">
//                       <label className="form-label text-end pe-3">
//                         Vacancies:
//                       </label>
//                       <div class="input-container">
//                         <input type="text" name="text" className="input" placeholder="Enter vacancies"
//                           value={vacancies}
//                           onChange={(e) => {
//                             const input = e.target.value;
//                             if (/^\d*$/.test(input)) {
//                               setVacancies(input);
//                             }
//                           }}
//                           pattern="[0-9]*"
//                           title="Please enter only numeric values"
//                           required
//                         />
//                         <div class="highlight"></div>
//                       </div>
//                       {/* <input
//                         type="text"
//                         className="form-control"
//                         value={vacancies}
//                         onChange={(e) => {
//                           const input = e.target.value;
//                           if (/^\d*$/.test(input)) {
//                             setVacancies(input);
//                           }
//                         }}
//                         pattern="[0-9]*"
//                         title="Please enter only numeric values"
//                         required
//                       /> */}
//                     </div>
//                     <div className="col-md-6">
//                       <label className="form-label text-end pe-3">
//                         Experience:
//                       </label>
//                       <div class="input-container">
//                         <input type="text" name="text" class="input" placeholder="Enter number(eg.0-1 year)"
//                           value={experience}
//                           onChange={(e) => {
//                             const input = e.target.value;
//                             if (/^[0-9.-]*(\s*years)?$/.test(input)) {
//                               setExperience(input);
//                             } else {
//                               setExperience("");
//                             }
//                           }}
//                           title="Enter a valid experience (e.g., 1.5 years or -2 years)"
//                           required
//                         />
//                         <div class="highlight"></div>
//                       </div>

//                       {/* <input
//                         type="text"
//                         className="form-control"
//                         value={experience}
//                         onChange={(e) => {
//                           const input = e.target.value;
//                           if (/^[0-9.-]*(\s*years)?$/.test(input)) {
//                             setExperience(input);
//                           } else {
//                             setExperience("");
//                           }
//                         }}
//                         title="Enter a valid experience (e.g., 1.5 years or -2 years)"
//                         required
//                       /> */}
//                     </div>
//                   </div>
//                 </div>

//                 <div className="mb-3">
//                   <div className="row justify-content-center align-items-center">
//                     <div className="col-md-6">
//                       <label className="form-label text-end pe-3">
//                         Job Location:
//                       </label>
//                       <div class="input-container">
//                         <input type="text" name="text" class="input" placeholder="Enter location" value={jobLocation}
//                           onChange={(e) => setJobLocation(e.target.value)}
//                           required
//                         />
//                         <div class="highlight"></div>
//                       </div>

//                       {/* <input
//                         type="text"
//                         className="form-control"
//                         value={jobLocation}
//                         onChange={(e) => setJobLocation(e.target.value)}
//                         required
//                       /> */}
//                     </div>
//                     <div className="col-md-6">
//                       <label className="form-label text-end pe-3">
//                         Role Type:
//                       </label>
//                       <div class="input-container">
//                         <select
//                           className="input"
//                           id="autoSizingSelect"
//                           value={roleType}
//                           onChange={handleRoleChange}
//                           required
//                         >
//                           <option value="" disabled>
//                             Choose...
//                           </option>
//                           {roleOptions.map((option) => (
//                             <option key={option.value} value={option.value}>
//                               {option.label}
//                             </option>
//                           ))}
//                         </select>
//                         <div class="highlight"></div>
//                       </div>
//                     </div>
//                   </div>
//                 </div>

//                 <div className="mb-3">
//                   <div className="row justify-content-center align-items-center">
//                     <div className="col-md-8">
//                       <label className="form-label text-end pe-3">
//                         Description:
//                       </label>
//                       <div class="input-container">
//                         <textarea
//                           className="input"
//                           value={description}
//                           onChange={(e) => setDescription(e.target.value)}
//                           required
//                         />
//                         <div class="highlight"></div>
//                       </div>



//                     </div>
//                   </div>
//                 </div>

//                 <div className="pt-3">
//                   <div className="row justify-content-center ">
//                     <div className="col-md-6 ">
//                       <button type="submit" className="btn btn-primary w-100"
//                         disabled={isLoading} // Disable button while loading
//                       >

//                         {isLoading ? (
//                           <span
//                             className="spinner-border spinner-border-sm"
//                             role="status"
//                             aria-hidden="true"
//                           ></span>
//                         ) : (
//                           " Post"
//                         )}

//                       </button>
//                     </div>
//                   </div>
//                 </div>
//               </form>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Jobposting;
